import { supabase } from '../config/supabase';



// Auth API - Now using Supabase directly
export const authAPI = {
  login: async (credentials) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password
    });

    if (error) throw error;
    return { data: { user: data.user, token: data.session.access_token } };
  },

  getMe: async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return { data: { user } };
  },

  logout: async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    return { data: { message: 'Logout successful' } };
  },

  signup: async (credentials) => {
    const { data, error } = await supabase.auth.signUp({
      email: credentials.email,
      password: credentials.password,
      options: {
        data: credentials.metadata || {}
      }
    });

    if (error) throw error;
    return { data };
  }
};

// Products API - Using Supabase directly
export const productsAPI = {
  getProducts: async (params = {}) => {
    let query = supabase
      .from('products')
      .select('*')
      .eq('is_active', true);

    if (params.search) {
      query = query.or(`name.ilike.%${params.search}%, product_code.ilike.%${params.search}%`);
    }

    if (params.season) {
      query = query.eq('season', params.season);
    }

    if (params.lowStock) {
      query = query.lte('stock', 0);
    }

    if (params.limit) {
      query = query.limit(params.limit);
    }

    if (params.page && params.limit) {
      const offset = (params.page - 1) * params.limit;
      query = query.range(offset, offset + params.limit - 1);
    }

    // Apply sorting
    if (params.sortField) {
      query = query.order(params.sortField, { ascending: params.sortDirection !== 'desc' });
    } else {
      query = query.order('created_at', { ascending: false });
    }

    const { data, error, count } = await query;

    if (error) throw error;

    return { data: { products: data, pagination: { totalProducts: count } } };
  },

  getProduct: async (id) => {
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('id', id)
      .eq('is_active', true)
      .single();

    if (error) throw error;
    return { data };
  },

  createProduct: async (productData, userId = null, userRole = null) => {
    // Normalize and ensure unique product_code
    const normalizeCode = (code) => {
      if (code === undefined || code === null) return null;
      const trimmed = String(code).trim();
      return trimmed.length === 0 ? null : trimmed;
    };

    let desiredCode = normalizeCode(productData.product_code || productData.productCode || productData.code);

    if (desiredCode) {
      // Ensure uniqueness by probing existing codes; append suffix if needed
      let uniqueCode = desiredCode;
      let counter = 1;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data: existing, error: checkError } = await supabase
          .from('products')
          .select('id')
          .eq('product_code', uniqueCode)
          .limit(1);
        if (checkError) {
          console.warn('Product code uniqueness check error:', checkError);
          break;
        }
        if (!existing || existing.length === 0) {
          break;
        }
        counter += 1;
        uniqueCode = `${desiredCode}-${counter}`;
      }
      desiredCode = uniqueCode;
    }

    const toDecimalOrNull = (value) => {
      if (value === undefined || value === null || String(value).trim() === '') return null;
      const normalized = String(value).replace(',', '.');
      const num = Number(normalized);
      return Number.isFinite(num) ? num : null;
    };

    const toIntegerOrZero = (value) => {
      if (value === undefined || value === null || String(value).trim() === '') return 0;
      const num = parseInt(String(value), 10);
      return Number.isFinite(num) ? num : 0;
    };

    // Build explicit payload mapped to DB columns and sanitize numerics
    const supabaseData = {
      name: productData.name,
      description: productData.description,
      product_code: desiredCode, // null allowed; unique constraint ignores nulls
      brand: productData.brand,
      season: productData.season,
      size: productData.size,
      color: productData.color,
      supplier: productData.supplier,
      image_url: productData.image_url,
      is_active: productData.is_active !== false,
      cost: toDecimalOrNull(productData.cost),
      stock: toIntegerOrZero(productData.stock),
      min_stock: toIntegerOrZero(productData.min_stock),
      created_by: userId || null
    };

    // Handle price based on user role
    // Warehouse role can only view prices, not edit them
    if (userRole === 'warehouse') {
      // Warehouse cannot set/edit prices, so we set a default value
      supabaseData.price = 0;
    } else {
      // Admin and other roles can set prices
      supabaseData.price = toDecimalOrNull(productData.price) || 0;
    }


    const { data, error } = await supabase
      .from('products')
      .insert([supabaseData])
      .select()
      .single();

    if (error) {
      console.error('Create product error:', error);
      throw error;
    }

    // If initial stock is provided, create a stock movement record
    const initialStock = toIntegerOrZero(productData.stock);
    if (initialStock > 0) {
      try {
        await stockMovementsAPI.create({
          productId: data.id,
          movementType: 'in',
          quantity: initialStock,
          previousStock: 0,
          newStock: initialStock,
          referenceType: 'initial_stock',
          referenceId: null,
          notes: `İlk Stok Girişi - ${initialStock} adet`,
          createdBy: userId || null
        });
      } catch (movementError) {
        console.error('Initial stock movement creation error:', movementError);
        // Don't throw error here as product creation was successful
      }
    }

    return { data };
  },

  updateProduct: async (id, productData, userId = null, userRole = null) => {
    const normalizeCode = (code) => {
      if (code === undefined || code === null) return null;
      const trimmed = String(code).trim();
      return trimmed.length === 0 ? null : trimmed;
    };

    let desiredCode = normalizeCode(productData.product_code || productData.productCode || productData.code);

    if (desiredCode) {
      // Ensure uniqueness against other products
      let uniqueCode = desiredCode;
      let counter = 1;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { data: existing, error: checkError } = await supabase
          .from('products')
          .select('id')
          .eq('product_code', uniqueCode)
          .neq('id', id)
          .limit(1);
        if (checkError) {
          console.warn('Product code uniqueness check error:', checkError);
          break;
        }
        if (!existing || existing.length === 0) {
          break;
        }
        counter += 1;
        uniqueCode = `${desiredCode}-${counter}`;
      }
      desiredCode = uniqueCode;
    }

    const toDecimalOrNull = (value) => {
      if (value === undefined || value === null || String(value).trim() === '') return null;
      const normalized = String(value).replace(',', '.');
      const num = Number(normalized);
      return Number.isFinite(num) ? num : null;
    };

    const toIntegerOrZero = (value) => {
      if (value === undefined || value === null || String(value).trim() === '') return 0;
      const num = parseInt(String(value), 10);
      return Number.isFinite(num) ? num : 0;
    };

    const supabaseData = {
      name: productData.name,
      description: productData.description,
      product_code: desiredCode,
      brand: productData.brand,
      season: productData.season,
      size: productData.size,
      color: productData.color,
      supplier: productData.supplier,
      image_url: productData.image_url,
      is_active: productData.is_active !== false,
      cost: toDecimalOrNull(productData.cost),
      min_stock: toIntegerOrZero(productData.min_stock),
      updated_by: userId || null
    };

    // Only include stock field if it's explicitly provided in productData
    if (productData.stock !== undefined) {
      supabaseData.stock = toIntegerOrZero(productData.stock);
    }


    // Handle price based on user role
    // Warehouse role can only view prices, not edit them
    if (userRole === 'warehouse') {
      // Don't update price for warehouse role
      // Price will remain unchanged in the database
    } else {
      // Admin and other roles can update prices
      supabaseData.price = toDecimalOrNull(productData.price) || 0;
    }

    const { data, error } = await supabase
      .from('products')
      .update(supabaseData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return { data };
  },

  deleteProduct: async (id) => {
    try {
      // First check if there are any sales records for this product
      const { data: salesData, error: salesError } = await supabase
        .from('sales')
        .select('id')
        .eq('product_id', id)
        .limit(1);

      if (salesError) {
        console.error('Error checking sales records:', salesError);
      }

      if (salesData && salesData.length > 0) {
        throw new Error('Bu ürün satış kayıtlarında kullanıldığı için silinemez. Önce ilgili satış kayıtlarını silin.');
      }

      // Check if there are any car transfers for this product
      const { data: carTransfersData, error: carTransfersError } = await supabase
        .from('car_transfers')
        .select('id')
        .eq('product_id', id)
        .limit(1);

      if (carTransfersError) {
        console.error('Error checking car transfers:', carTransfersError);
      }

      if (carTransfersData && carTransfersData.length > 0) {
        throw new Error('Bu ürün araç transferlerinde kullanıldığı için silinemez. Önce ilgili transfer kayıtlarını silin.');
      }

      // If no related records, proceed with deletion
      const { data, error } = await supabase
        .from('products')
        .delete()
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Delete product error:', error);
        throw error;
      }

      return { data };
    } catch (error) {
      console.error('Delete product error:', error);
      throw error;
    }
  },

  searchProductsForPOS: async (params = {}) => {
    let query = supabase
      .from('products')
      .select('*')
      .eq('is_active', true);

    if (params.search) {
      query = query.or(`name.ilike.%${params.search}%, product_code.ilike.%${params.search}%`);
    }

    const { data, error } = await query.limit(20);

    if (error) throw error;
    return { data };
  },


  getFilterOptions: async () => {
    // Get unique brands from products
    const { data, error } = await supabase
      .from('products')
      .select('brand')
      .eq('is_active', true)
      .not('brand', 'is', null);

    if (error) throw error;

    const uniqueBrands = [...new Set(data.map(p => p.brand).filter(Boolean))];
    return { data: { brands: uniqueBrands } };
  },

  getLowStockProducts: async () => {
    const { data, error, count } = await supabase
      .from('products')
      .select('*', { count: 'exact' })
      .eq('is_active', true)
      .lte('stock', 5);

    if (error) throw error;
    return { data: { products: data, total: count || data?.length || 0 } };
  },

  getProductStats: async (params = {}) => {
    try {
      const { startDate: providedStartDate, endDate: providedEndDate } = params;

      // Use provided date range or calculate based on filter
      let startDate, endDate;

      if (providedStartDate && providedEndDate) {
        startDate = new Date(providedStartDate);
        endDate = new Date(providedEndDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
      } else {
        // Default to today if no dates provided
        startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
      }


      // Get total products (all time)
      const { count: totalProducts, error: totalError } = await supabase
        .from('products')
        .select('*', { count: 'exact' })
        .eq('is_active', true);

      if (totalError) throw totalError;

      // Get low stock products (all time)
      const { data: lowStockProducts, count: lowStockCount, error: lowStockError } = await supabase
        .from('products')
        .select('*', { count: 'exact' })
        .eq('is_active', true)
        .lte('stock', 5);

      if (lowStockError) throw lowStockError;

      // Get out of stock products (all time)
      const { count: outOfStockCount, error: outOfStockError } = await supabase
        .from('products')
        .select('*', { count: 'exact' })
        .eq('is_active', true)
        .lte('stock', 0);

      if (outOfStockError) throw outOfStockError;

      // Get products sold in the selected period
      const { data: soldProducts, error: soldError } = await supabase
        .from('sales')
        .select(`
          product_id,
          products (
            id,
            name,
            stock
          )
        `)
        .gte('sale_date', startDate.toISOString())
        .lte('sale_date', endDate.toISOString());

      if (soldError) {
        console.error('Error fetching sold products:', soldError);
      }

      // Get products created in the selected period
      const { count: newProductsCount, error: newProductsError } = await supabase
        .from('products')
        .select('*', { count: 'exact' })
        .eq('is_active', true)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());

      if (newProductsError) {
        console.error('Error fetching new products:', newProductsError);
      }

      return {
        data: {
          total: totalProducts || 0,
          lowStock: lowStockCount || 0,
          outOfStock: outOfStockCount || 0,
          soldInPeriod: soldProducts?.length || 0,
          newInPeriod: newProductsCount || 0,
          products: lowStockProducts || []
        }
      };
    } catch (error) {
      console.error('Error fetching product stats:', error);
      throw error;
    }
  },

  updateStock: async (productId, quantityToAdd, movementData = {}) => {
    try {

      // First get current product data including cost to preserve it
      const { data: product, error: fetchError } = await supabase
        .from('products')
        .select('stock, cost, price')
        .eq('id', productId)
        .single();

      if (fetchError) throw fetchError;

      const previousStock = product.stock || 0;
      const newStock = previousStock + quantityToAdd;

      // Update stock while preserving cost and price
      const { data, error } = await supabase
        .from('products')
        .update({
          stock: newStock,
          cost: product.cost, // Preserve the purchase price even if null
          price: product.price // Preserve the selling price even if null
        })
        .eq('id', productId)
        .select()
        .single();

      if (error) throw error;

      // Always create stock movement record if movementData is provided, regardless of cost
      if (movementData.movementType) {

        // Prepare movement data with all required fields
        const completeMovementData = {
          productId: productId,
          movementType: movementData.movementType,
          quantity: Math.abs(quantityToAdd),
          previousStock: previousStock,
          newStock: newStock,
          referenceType: movementData.referenceType || 'manual_adjustment',
          referenceId: movementData.referenceId || null,
          notes: movementData.notes || `Stok Girişi - ${Math.abs(quantityToAdd)} adet`,
          createdBy: movementData.createdBy || null
        };


        try {
          await stockMovementsAPI.create(completeMovementData);
        } catch (movementError) {
          console.error('Stock movement creation error:', movementError);

          // Retry with minimal data if full creation fails
          try {
            await stockMovementsAPI.create({
              productId: productId,
              movementType: movementData.movementType,
              quantity: Math.abs(quantityToAdd),
              previousStock: previousStock,
              newStock: newStock,
              referenceType: 'manual_adjustment',
              referenceId: null,
              notes: `Stok Hareketi - ${Math.abs(quantityToAdd)} adet`,
              createdBy: movementData.createdBy || null
            });
          } catch (retryError) {
            console.error('Retry stock movement creation also failed:', retryError);
            // Final fallback - create a basic movement record directly
            try {
              const { error: fallbackError } = await supabase
                .from('stock_movements')
                .insert([{
                  product_id: productId,
                  movement_type: movementData.movementType,
                  quantity: Math.abs(quantityToAdd),
                  previous_stock: previousStock,
                  new_stock: newStock,
                  reference_type: 'manual_adjustment',
                  notes: `Stok Girişi - ${Math.abs(quantityToAdd)} adet`,
                  created_by: movementData.createdBy || null
                }])
                .select()
                .single();

              if (fallbackError) {
                console.error('Fallback stock movement creation failed:', fallbackError);
                throw fallbackError;
              }

            } catch (fallbackError) {
              console.error('All stock movement creation attempts failed:', fallbackError);
              // Don't throw here - stock was updated successfully, just movement failed
              console.warn('Stock was updated but movement record could not be created');
            }
          }
        }
      }

      return { data };
    } catch (error) {
      console.error('Update stock error:', error);
      throw error;
    }
  }
};

// Stock Movements API
export const stockMovementsAPI = {
  // Create a stock movement record
  create: async (movementData) => {
    try {

      // Ensure all required fields are present and properly formatted
      const movementRecord = {
        product_id: movementData.productId,
        movement_type: movementData.movementType,
        quantity: Math.abs(movementData.quantity || 0),
        previous_stock: movementData.previousStock || 0,
        new_stock: movementData.newStock || 0,
        reference_type: movementData.referenceType || 'manual_adjustment',
        reference_id: movementData.referenceId || null,
        notes: movementData.notes || `Stok Hareketi - ${Math.abs(movementData.quantity || 0)} adet`,
        created_by: movementData.createdBy || null
      };


      const { data, error } = await supabase
        .from('stock_movements')
        .insert([movementRecord])
        .select()
        .single();

      if (error) {
        console.error('Supabase error creating stock movement:', error);
        throw error;
      }

      return { data };
    } catch (error) {
      console.error('Create stock movement error:', error);
      console.error('Movement data that failed:', movementData);
      throw error;
    }
  },

  // Get stock movements for a product (excluding sales, as sales are inherently stock outflows)
  getByProduct: async (productId, params = {}) => {
    try {
      let query = supabase
        .from('stock_movements')
        .select('*')
        .eq('product_id', productId)
        .neq('reference_type', 'sale')
        .neq('reference_type', 'car_sale')
        .order('created_at', { ascending: false });

      if (params.limit) {
        query = query.limit(params.limit);
      }

      if (params.startDate) {
        query = query.gte('created_at', params.startDate);
      }

      if (params.endDate) {
        query = query.lte('created_at', params.endDate);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data };
    } catch (error) {
      console.error('Get stock movements error:', error);
      throw error;
    }
  },

  // Get all stock movements with filters (excluding sales, as sales are inherently stock outflows)
  getAll: async (params = {}) => {
    try {
      let query = supabase
        .from('stock_movements')
        .select(`
  *,
  products(
  id,
  name,
  brand,
  product_code
  )
  `)
        .neq('reference_type', 'sale')
        .neq('reference_type', 'car_sale')
        .order('created_at', { ascending: false });

      if (params.movementType) {
        query = query.eq('movement_type', params.movementType);
      }

      if (params.referenceType) {
        query = query.eq('reference_type', params.referenceType);
      }

      if (params.startDate) {
        query = query.gte('created_at', params.startDate);
      }

      if (params.endDate) {
        query = query.lte('created_at', params.endDate);
      }

      if (params.limit) {
        query = query.limit(params.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { data };
    } catch (error) {
      console.error('Get all stock movements error:', error);
      throw error;
    }
  },

  // Delete a stock movement and reverse its effect on stock
  deleteStockMovement: async (movementId) => {
    try {
      // Get the movement record to understand the type and quantity
      const { data: movement, error: fetchError } = await supabase
        .from('stock_movements')
        .select('product_id, movement_type, quantity')
        .eq('id', movementId)
        .single();

      if (fetchError) throw fetchError;

      // Get current product stock
      const { data: product, error: productError } = await supabase
        .from('products')
        .select('stock')
        .eq('id', movement.product_id)
        .single();

      if (productError) throw productError;

      // Calculate the reverse stock change
      // If movement was 'in' or 'transfer_in', we need to decrease stock
      // If movement was 'out' or 'transfer_out', we need to increase stock
      const isInflow = movement.movement_type === 'in' || movement.movement_type === 'transfer_in';
      const stockDelta = isInflow ? -movement.quantity : movement.quantity;
      const newStock = Math.max(0, product.stock + stockDelta);

      // Update product stock
      const { error: stockError } = await supabase
        .from('products')
        .update({ stock: newStock })
        .eq('id', movement.product_id);

      if (stockError) throw stockError;

      // Delete the movement record
      const { error: deleteError } = await supabase
        .from('stock_movements')
        .delete()
        .eq('id', movementId);

      if (deleteError) throw deleteError;

      return { data: { success: true, productId: movement.product_id, stockChange: stockDelta } };
    } catch (error) {
      console.error('Delete stock movement error:', error);
      throw error;
    }
  }
};

// Customers API - Using Supabase directly
export const customersAPI = {
  getCustomers: async (params = {}) => {
    let query = supabase
      .from('customers')
      .select('*', { count: 'exact' })
      .eq('is_active', true);

    if (params.search) {
      query = query.or(`first_name.ilike.%${params.search}%, last_name.ilike.%${params.search}%, phone.ilike.%${params.search}%`);
    }

    if (params.limit) {
      query = query.limit(params.limit);
    }

    if (params.page && params.limit) {
      const offset = (params.page - 1) * params.limit;
      query = query.range(offset, offset + params.limit - 1);
    }

    // Apply sorting
    if (params.sortBy) {
      // Map frontend column names to backend column names if necessary
      const sortColumnMap = {
        'firstName': 'first_name',
        'lastName': 'last_name',
        'phone': 'phone',
        'city': 'city',
        'createdAt': 'created_at',
        'updatedAt': 'updated_at',
        'balance': 'balance'
      };

      const sortField = sortColumnMap[params.sortBy] || params.sortBy;
      query = query.order(sortField, { ascending: params.sortOrder === 'asc' });
    } else {
      query = query.order('created_at', { ascending: false });
    }

    const { data, error, count } = await query;

    if (error) throw error;

    // Convert snake_case to camelCase for frontend
    const customers = data.map(customer => ({
      id: customer.id,
      firstName: customer.first_name,
      lastName: customer.last_name,
      fullName: `${customer.first_name} ${customer.last_name}`.trim(),
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      city: customer.city || '',
      district: customer.district || '',
      tcNumber: customer.tc_number || '',
      balance: customer.balance || 0,
      isActive: customer.is_active !== false,
      createdAt: customer.created_at,
      updatedAt: customer.updated_at
    }));

    const totalPages = params.limit ? Math.ceil(count / params.limit) : 1;
    return { data: { customers, pagination: { totalCustomers: count, totalPages } } };
  },

  getCustomer: async (id) => {
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .eq('id', id)
      .eq('is_active', true)
      .single();

    if (error) throw error;

    // Convert snake_case to camelCase for frontend
    const customer = {
      id: data.id,
      firstName: data.first_name,
      lastName: data.last_name,
      fullName: `${data.first_name} ${data.last_name}`.trim(),
      phone: data.phone || '',
      email: data.email || '',
      address: data.address || '',
      city: data.city || '',
      district: data.district || '',
      tcNumber: data.tc_number || '',
      balance: data.balance || 0,
      isActive: data.is_active !== false,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };

    return { data: { customer } };
  },

  createCustomer: async (customerData, userId = null) => {
    // Get current user from Supabase to ensure we have the correct user ID
    const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

    if (userError) {
      console.error('Error getting current user:', userError);
      throw userError;
    }

    // Use the current user ID if no userId is provided
    const actualUserId = currentUser?.id || userId;

    // Convert camelCase to snake_case for Supabase
    const supabaseData = {
      first_name: customerData.firstName,
      last_name: customerData.lastName,
      phone: customerData.phone,
      email: customerData.email,
      address: customerData.address,
      city: customerData.city,
      district: customerData.district,
      tc_number: customerData.tcNumber,
      balance: 0,
      is_active: true,
      created_by: actualUserId
    };

    const { data, error } = await supabase
      .from('customers')
      .insert([supabaseData])
      .select()
      .single();

    if (error) throw error;
    return { data };
  },

  updateCustomer: async (id, customerData, userId = null) => {

    // Get current user from Supabase to ensure we have the correct user ID
    const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

    if (userError) {
      console.error('Error getting current user:', userError);
      throw userError;
    }

    // Use the current user ID if no userId is provided or if it's different
    const actualUserId = currentUser?.id || userId;

    // Convert camelCase to snake_case for Supabase
    const supabaseData = {
      first_name: customerData.firstName,
      last_name: customerData.lastName,
      phone: customerData.phone,
      email: customerData.email,
      address: customerData.address,
      city: customerData.city,
      district: customerData.district,
      tc_number: customerData.tcNumber,
      updated_by: actualUserId
    };


    const { data, error } = await supabase
      .from('customers')
      .update(supabaseData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Supabase update error:', error);
      throw error;
    }

    return { data };
  },

  deleteCustomer: async (id) => {
    // Permanently delete customer and all related data due to CASCADE constraints
    const { data, error } = await supabase
      .from('customers')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return { data };
  },

  getStats: async (params = {}) => {
    try {

      const { dateRange = 'today', selectedDate = new Date(), startDate: providedStartDate, endDate: providedEndDate } = params;

      // Use provided date range or calculate based on filter
      let startDate, endDate;

      if (providedStartDate && providedEndDate) {
        startDate = new Date(providedStartDate);
        endDate = new Date(providedEndDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
      } else {
        const targetDate = new Date(selectedDate);

        switch (dateRange) {
          case 'today':
            startDate = new Date(targetDate);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(targetDate);
            endDate.setHours(23, 59, 59, 999);
            break;
          case 'week':
            endDate = new Date(targetDate);
            endDate.setHours(23, 59, 59, 999);
            startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - 6);
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'month':
            const year = targetDate.getFullYear();
            const month = targetDate.getMonth();
            startDate = new Date(year, month, 1);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(year, month + 1, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
          default:
            startDate = new Date(targetDate);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(targetDate);
            endDate.setHours(23, 59, 59, 999);
        }
      }


      // Get total customers (all time)
      const { data: allCustomers, count: totalCustomers, error: totalError } = await supabase
        .from('customers')
        .select('*', { count: 'exact' })
        .eq('is_active', true);


      if (totalError) {
        console.error('Error fetching total customers:', totalError);
        throw totalError;
      }

      // Get new customers in the selected period
      const { data: newCustomers, count: newInPeriod, error: newError } = await supabase
        .from('customers')
        .select('*', { count: 'exact' })
        .eq('is_active', true)
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString());


      if (newError) {
        console.error('Error fetching new customers:', newError);
        throw newError;
      }

      // Get active customers (customers with operations in the selected period)
      const { data: activeCustomers, error: activeError } = await supabase
        .from('product_operations')
        .select('customer_id')
        .gte('created_at', startDate.toISOString())
        .lte('created_at', endDate.toISOString())
        .not('customer_id', 'is', null);


      if (activeError) {
        console.error('Error fetching active customers:', activeError);
        throw activeError;
      }

      // Get unique active customers
      const uniqueActiveCustomers = [...new Set(activeCustomers.map(op => op.customer_id))];

      const result = {
        data: {
          totalCustomers: totalCustomers || allCustomers?.length || 0,
          newThisPeriod: newInPeriod || newCustomers?.length || 0,
          activeCustomers: uniqueActiveCustomers.length
        }
      };

      return result;
    } catch (error) {
      console.error('Error fetching customer stats:', error);
      throw error;
    }
  },

  testConnection: async () => {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('id, first_name, last_name')
        .limit(5);

      return { data, error };
    } catch (error) {
      console.error('Test connection error:', error);
      return { data: null, error };
    }
  },

  getActiveCustomers: async (days = 30) => {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      // Get customers with operations in the specified period
      const { data: operations, error: operationsError } = await supabase
        .from('product_operations')
        .select(`
          customer_id,
          customers!inner(
            id,
            first_name,
            last_name,
            phone,
            email,
            balance,
            created_at
          )
        `)
        .gte('created_at', startDate.toISOString())
        .not('customer_id', 'is', null);

      if (operationsError) throw operationsError;

      // Group by customer and get unique customers
      const customerMap = new Map();
      operations.forEach(op => {
        if (op.customers && !customerMap.has(op.customer_id)) {
          // Combine first_name and last_name into name
          const fullName = `${op.customers.first_name || ''} ${op.customers.last_name || ''}`.trim();
          customerMap.set(op.customer_id, {
            ...op.customers,
            name: fullName,  // Add the combined name field
            lastOperationDate: op.created_at
          });
        }
      });

      const activeCustomers = Array.from(customerMap.values())
        .sort((a, b) => new Date(b.lastOperationDate) - new Date(a.lastOperationDate));

      return { data: { customers: activeCustomers } };
    } catch (error) {
      console.error('Error fetching active customers:', error);
      throw error;
    }
  },

  getDeletedCustomers: async (params = {}) => {
    let query = supabase
      .from('customers')
      .select('*')
      .eq('is_active', false);

    if (params.limit) {
      query = query.limit(params.limit);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { data: { customers: data } };
  },

  restoreCustomer: async (id) => {
    const { data, error } = await supabase
      .from('customers')
      .update({ is_active: true })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return { data };
  },

  permanentDeleteCustomer: async (id) => {
    const { data, error } = await supabase
      .from('customers')
      .delete()
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return { data };
  },

  addBalanceTransaction: async (customerId, transactionData) => {
    // Get current user from Supabase
    const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

    if (userError) {
      console.error('Error getting current user:', userError);
      throw userError;
    }

    // Normalize type: treat any positive amount as 'payment' unless explicitly set
    const normalizedType = transactionData.type || (parseFloat(transactionData.amount) > 0 ? 'payment' : 'debit');

    // Create balance transaction record
    const { data, error } = await supabase
      .from('balance_transactions')
      .insert([{
        customer_id: customerId,
        detail_id: transactionData.detail_id,
        type: normalizedType,
        amount: transactionData.amount,
        description: transactionData.description,
        transaction_date: transactionData.date,
        notes: transactionData.notes,
        created_by: currentUser?.id
      }])
      .select()
      .single();

    if (error) throw error;

    // Update customer balance
    const balanceChange = normalizedType === 'payment'
      ? transactionData.amount
      : -transactionData.amount;

    const { error: updateError } = await supabase.rpc('update_customer_balance', {
      customer_id: customerId,
      balance_change: balanceChange
    });

    if (updateError) throw updateError;

    // Update financial summary for the transaction date
    try {
      await dashboardAPI.saveFinancialSummary(transactionData.date);
    } catch (saveError) {
      console.error('Failed to save financial summary after balance transaction:', saveError);
      // Do not throw; balance is already updated. Dashboard can refresh later.
    }

    return { data };
  },

  getBalanceTransactions: async (customerId, params = {}) => {
    let query = supabase
      .from('balance_transactions')
      .select('*')
      .eq('customer_id', customerId)
      .order('transaction_date', { ascending: false })
      .order('created_at', { ascending: false });

    if (params.limit) {
      query = query.limit(params.limit);
    }

    const { data, error } = await query;

    if (error) throw error;
    return { data: { transactions: data } };
  },

  getBalanceTransactionsByDetail: async (detailId) => {
    const { data, error } = await supabase
      .from('balance_transactions')
      .select('*')
      .eq('detail_id', detailId)
      .order('transaction_date', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching balance transactions by detail:', error);
      throw error;
    }

    return { data: { transactions: data } };
  },

  deleteBalanceTransaction: async (transactionId) => {

    try {
      // Get current user from Supabase
      const { error: userError } = await supabase.auth.getUser();

      if (userError) {
        console.error('Error getting current user:', userError);
        throw userError;
      }

      // First, get the transaction to know the customer, amount and date
      const { data: transaction, error: fetchError } = await supabase
        .from('balance_transactions')
        .select('customer_id, type, amount, transaction_date')
        .eq('id', transactionId)
        .single();

      if (fetchError) {
        console.error('Error fetching transaction:', fetchError);
        throw fetchError;
      }

      // Delete the transaction
      const { data, error } = await supabase
        .from('balance_transactions')
        .delete()
        .eq('id', transactionId)
        .select()
        .single();

      if (error) {
        console.error('Database delete error:', error);
        throw error;
      }

      // Update customer balance (reverse the transaction)
      const balanceChange = transaction.type === 'payment'
        ? -transaction.amount
        : transaction.amount;

      const { error: balanceError } = await supabase.rpc('update_customer_balance', {
        customer_id: transaction.customer_id,
        balance_change: balanceChange
      });

      if (balanceError) {
        console.error('Error updating customer balance:', balanceError);
        throw balanceError;
      }

      // Update financial summary for that transaction date
      try {
        await dashboardAPI.saveFinancialSummary(transaction.transaction_date);
      } catch (saveError) {
        console.error('Failed to save financial summary after deleting balance transaction:', saveError);
      }

      return { data };
    } catch (error) {
      console.error('deleteBalanceTransaction error:', error);
      throw error;
    }
  },

  addProductOperation: async (customerId, operationData) => {
    try {
      // First, test if the table exists by trying to select from it
      const { error: testError } = await supabase
        .from('product_operations')
        .select('id')
        .limit(1);

      if (testError) {
        console.error('Table test error - product_operations table might not exist:', testError);
        console.error('Error details:', {
          message: testError.message,
          details: testError.details,
          hint: testError.hint,
          code: testError.code
        });
        throw new Error(`Database table error: ${testError.message}. Please check if the product_operations table exists in your database.`);
      }


      // Get current user from Supabase
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        console.error('Error getting current user:', userError);
        throw userError;
      }


      const insertData = {
        customer_id: customerId,
        brand: operationData.brand,
        product_name: operationData.productName,
        model: operationData.model,
        serial_number: operationData.serialNumber,
        operation_description: operationData.notes || '', // Use notes as operation description
        notes: operationData.notes,
        created_by: currentUser?.id
      };


      // Create product operation record with new structure
      const { data, error } = await supabase
        .from('product_operations')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('Database insert error:', error);
        console.error('Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      return { data };
    } catch (error) {
      console.error('addProductOperation error:', error);
      throw error;
    }
  },

  getProductOperations: async (customerId, params = {}) => {

    let query = supabase
      .from('product_operations')
      .select(`
        *,
        details:product_operation_details(*)
      `)
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false });

    if (params.limit) {
      query = query.limit(params.limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching product operations:', error);
      throw error;
    }


    // Convert snake_case to camelCase for frontend
    const operations = data.map(operation => ({
      id: operation.id,
      brand: operation.brand,
      productName: operation.product_name,
      model: operation.model,
      serialNumber: operation.serial_number,
      operationDescription: operation.operation_description,
      notes: operation.notes,
      date: operation.created_at,
      customerId: operation.customer_id,
      createdBy: operation.created_by,
      details: operation.details ? operation.details.map(detail => ({
        id: detail.id,
        operationType: detail.operation_type,
        operationDescription: detail.operation_description,
        created_at: detail.created_at,
        updated_at: detail.updated_at
      })) : []
    }));

    return { data: { operations } };
  },

  updateProductOperation: async (operationId, operationData) => {
    // Get current user from Supabase
    const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

    if (userError) {
      console.error('Error getting current user:', userError);
      throw userError;
    }

    // Update product operation record
    const { data, error } = await supabase
      .from('product_operations')
      .update({
        brand: operationData.brand,
        product_name: operationData.productName,
        model: operationData.model,
        serial_number: operationData.serialNumber,
        operation_description: operationData.notes || '', // Use notes as operation description
        notes: operationData.notes,
        updated_by: currentUser?.id
      })
      .eq('id', operationId)
      .select()
      .single();

    if (error) throw error;

    return { data };
  },

  deleteProductOperation: async (operationId) => {
    // First, get the operation details to identify related balance transactions
    const { data: operation, error: fetchError } = await supabase
      .from('product_operations')
      .select('customer_id, brand, product_name, model')
      .eq('id', operationId)
      .single();

    if (fetchError) throw fetchError;

    // Delete related balance transactions for this product
    const productInfo = `${operation.brand} ${operation.product_name} ${operation.model || ''}`.trim();
    const { error: balanceDeleteError } = await supabase
      .from('balance_transactions')
      .delete()
      .eq('customer_id', operation.customer_id)
      .like('notes', `%${productInfo}%`);

    if (balanceDeleteError) {
      console.error('Error deleting related balance transactions:', balanceDeleteError);
      // Continue with operation deletion even if balance deletion fails
    }

    // Delete the product operation
    const { data, error } = await supabase
      .from('product_operations')
      .delete()
      .eq('id', operationId)
      .select()
      .single();

    if (error) throw error;
    return { data };
  },

  // Product Operation Details API
  addProductOperationDetail: async (productOperationId, detailData) => {

    try {
      // Get current user from Supabase
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        console.error('Error getting current user:', userError);
        throw userError;
      }

      const insertData = {
        product_operation_id: productOperationId,
        operation_type: detailData.operationType,
        operation_description: detailData.operationDescription || null,
        operation_date: new Date().toISOString(),
        is_active: detailData.isActive !== undefined ? detailData.isActive : true,
        notes: detailData.notes || null,
        created_by: currentUser?.id
      };


      const { data, error } = await supabase
        .from('product_operation_details')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        console.error('Database insert error:', error);
        throw error;
      }

      return { data };
    } catch (error) {
      console.error('addProductOperationDetail error:', error);
      throw error;
    }
  },

  getProductOperationDetails: async (productOperationId) => {

    const { data, error } = await supabase
      .from('product_operation_details')
      .select('*')
      .eq('product_operation_id', productOperationId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching operation details:', error);
      throw error;
    }


    // Convert snake_case to camelCase for frontend
    const details = data.map(detail => ({
      id: detail.id,
      productOperationId: detail.product_operation_id,
      operationType: detail.operation_type,
      operationDescription: detail.operation_description,
      technicianName: detail.technician_name,
      operationDate: detail.operation_date,
      estimatedCompletionDate: detail.estimated_completion_date,
      actualCompletionDate: detail.actual_completion_date,
      cost: detail.cost,
      partsUsed: detail.parts_used ? JSON.parse(detail.parts_used) : null,
      status: detail.status,
      isActive: detail.is_active !== undefined ? detail.is_active : true,
      notes: detail.notes,
      createdAt: detail.created_at,
      updatedAt: detail.updated_at,
      createdBy: detail.created_by
    }));

    return { data: { details } };
  },

  updateProductOperationDetail: async (detailId, detailData) => {

    try {
      // Get current user from Supabase
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) {
        console.error('Error getting current user:', userError);
        throw userError;
      }

      const updateData = {
        operation_type: detailData.operationType,
        operation_description: detailData.operationDescription || null,
        is_active: detailData.isActive !== undefined ? detailData.isActive : true,
        notes: detailData.notes || null,
        updated_by: currentUser?.id
      };


      const { data, error } = await supabase
        .from('product_operation_details')
        .update(updateData)
        .eq('id', detailId)
        .select()
        .single();

      if (error) {
        console.error('Database update error:', error);
        throw error;
      }

      return { data };
    } catch (error) {
      console.error('updateProductOperationDetail error:', error);
      throw error;
    }
  },

  deleteProductOperationDetail: async (detailId) => {
    const { data, error } = await supabase
      .from('product_operation_details')
      .delete()
      .eq('id', detailId)
      .select()
      .single();

    if (error) throw error;
    return { data };
  }
};

// User Roles API - Using Supabase Auth User Metadata
export const userRolesAPI = {
  getUserRole: async (userId) => {
    // Get user from auth with metadata
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error) throw error;

    const role = user?.user_metadata?.role || 'reception';
    return { data: { role } };
  },

  setUserRole: async (userId, role, createdBy) => {
    // Update user metadata with new role
    const { data, error } = await supabase.auth.updateUser({
      data: { role: role }
    });

    if (error) throw error;
    return { data };
  },

  getAllUsersWithRoles: async () => {
    // Note: We can't directly query all users from frontend
    // This would require a backend function or admin privileges
    // For now, return current user info
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error) throw error;

    if (!user) {
      return { data: [] };
    }

    const role = user.user_metadata?.role || 'reception';
    return {
      data: [{
        id: user.id,
        user_id: user.id,
        role: role,
        created_at: user.created_at,
        auth: {
          users: {
            id: user.id,
            email: user.email,
            created_at: user.created_at,
            email_confirmed_at: user.email_confirmed_at
          }
        }
      }]
    };
  },

  createUser: async (userData) => {
    // Create user in Supabase Auth with role in metadata
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
      options: {
        data: {
          role: userData.role
        }
      }
    });

    if (authError) throw authError;

    if (!authData.user) {
      throw new Error('Kullanıcı oluşturulamadı');
    }

    return { data: { user: authData.user, role: userData.role } };
  },

  deleteUser: async (userId) => {
    // Note: User deletion from auth requires admin privileges
    // For now, we'll just return success
    console.warn('Kullanıcı silme işlemi admin panelinden yapılmalıdır.');
    return { data: { success: true } };
  },

  // Get all users from auth (for debugging)
  getAllAuthUsers: async () => {
    // This is a workaround - we can't directly query all users from frontend
    // This would require a backend function or admin privileges
    const { data: { user }, error } = await supabase.auth.getUser();

    if (error) throw error;

    if (!user) {
      return { data: [] };
    }

    const role = user.user_metadata?.role || 'reception';
    return {
      data: [{
        id: user.id,
        user_id: user.id,
        role: role,
        created_at: user.created_at,
        auth: {
          users: {
            id: user.id,
            email: user.email,
            created_at: user.created_at,
            email_confirmed_at: user.email_confirmed_at
          }
        }
      }]
    };
  }
};


// Health check - Using Supabase connection test
export const healthAPI = {
  check: async () => {
    try {
      const { error } = await supabase
        .from('products')
        .select('count')
        .limit(1);

      if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows found" which is fine
        throw error;
      }

      return {
        data: {
          status: 'OK',
          message: 'Supabase connection is healthy',
          timestamp: new Date().toISOString()
        }
      };
    } catch (error) {
      throw new Error(`Health check failed: ${error.message}`);
    }
  },
};

// Sales API - Using Supabase directly
export const salesAPI = {
  // Create a new sale
  createSale: async (saleData, userId) => {
    try {
      // First get current stock to create movement record
      // eslint-disable-next-line no-unused-vars
      const { data: _, error: fetchError } = await supabase
        .from('products')
        .select('stock')
        .eq('id', saleData.productId)
        .single();

      if (fetchError) throw fetchError;

      const newStock = saleData.newStock;

      const { data, error } = await supabase
        .from('sales')
        .insert([{
          product_id: saleData.productId,
          quantity: saleData.quantity,
          unit_price: saleData.unitPrice,
          total_price: saleData.totalPrice,
          user_id: userId,
          sale_date: new Date().toISOString(),
          notes: saleData.notes || null
        }])
        .select()
        .single();

      if (error) throw error;

      // Update product stock
      const { error: stockError } = await supabase
        .from('products')
        .update({
          stock: newStock
        })
        .eq('id', saleData.productId);

      if (stockError) throw stockError;

      // Update financial summary for the sale date (must be awaited)
      const saleDate = new Date(data.sale_date);
      const savePromise = dashboardAPI.saveFinancialSummary(saleDate);

      // Start saving but don't wait for it to complete
      savePromise.catch(() => {
        // Silent fail - don't log errors
      });

      return { data };
    } catch (error) {
      console.error('Create sale error:', error);
      throw error;
    }
  },

  // Get sales statistics
  getSalesStats: async (params = {}) => {
    try {
      const { dateRange = 'today', selectedDate = new Date(), startDate: providedStartDate, endDate: providedEndDate } = params;

      // Use provided date range or calculate based on filter
      let startDate, endDate;

      if (providedStartDate && providedEndDate) {
        startDate = new Date(providedStartDate);
        endDate = new Date(providedEndDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
      } else {
        const targetDate = new Date(selectedDate);

        switch (dateRange) {
          case 'today':
            startDate = new Date(targetDate);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(targetDate);
            endDate.setHours(23, 59, 59, 999);
            break;
          case 'week':
            endDate = new Date(targetDate);
            endDate.setHours(23, 59, 59, 999);
            startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - 6);
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'month':
            const year = targetDate.getFullYear();
            const month = targetDate.getMonth();
            startDate = new Date(year, month, 1);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(year, month + 1, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
          default:
            startDate = new Date(targetDate);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(targetDate);
            endDate.setHours(23, 59, 59, 999);
        }
      }


      // Get stock sales in the selected period
      const stockPeriodPromise = supabase
        .from('sales')
        .select('total_price, quantity, sale_date')
        .gte('sale_date', startDate.toISOString())
        .lte('sale_date', endDate.toISOString());

      // Get car sales in the selected period
      const carPeriodPromise = supabase
        .from('car_sales')
        .select('total_price, quantity, sale_date')
        .gte('sale_date', startDate.toISOString())
        .lte('sale_date', endDate.toISOString());

      // Get total counts (all time)
      const stockCountPromise = supabase
        .from('sales')
        .select('*', { count: 'exact' });
      const carCountPromise = supabase
        .from('car_sales')
        .select('*', { count: 'exact' });

      const [stockPeriodRes, carPeriodRes, stockCountRes, carCountRes] = await Promise.all([
        stockPeriodPromise, carPeriodPromise, stockCountPromise, carCountPromise
      ]);

      const { data: stockPeriod = [], error: stockPeriodError } = stockPeriodRes;
      const { error: carPeriodError } = carPeriodRes;
      const { count: stockTotalCount = 0, error: stockCountError } = stockCountRes;
      const { error: carCountError } = carCountRes;

      if (stockPeriodError) throw stockPeriodError;
      if (carPeriodError) throw carPeriodError;
      if (stockCountError) throw stockCountError;
      if (carCountError) throw carCountError;

      // For dashboard "Kasa Satışları" we want STOCK sales only
      const periodTotal = (stockPeriod || []).reduce((sum, sale) => sum + (parseFloat(sale.total_price) || 0), 0);
      const periodQuantity = (stockPeriod || []).reduce((sum, sale) => sum + (parseFloat(sale.quantity) || 0), 0);
      const totalSales = stockTotalCount || 0;

      // Get previous period for comparison
      let previousPeriodTotal = 0;
      if (dateRange === 'today') {
        const yesterday = new Date(startDate);
        yesterday.setDate(yesterday.getDate() - 1);
        const yesterdayEnd = new Date(yesterday);
        yesterdayEnd.setHours(23, 59, 59, 999);

        const { data: yStock = [] } = await supabase
          .from('sales')
          .select('total_price')
          .gte('sale_date', yesterday.toISOString())
          .lte('sale_date', yesterdayEnd.toISOString());
        previousPeriodTotal = (yStock || []).reduce((sum, s) => sum + (parseFloat(s.total_price) || 0), 0);
      } else if (dateRange === 'week') {
        const previousWeekStart = new Date(startDate);
        previousWeekStart.setDate(previousWeekStart.getDate() - 7);
        const previousWeekEnd = new Date(startDate);
        previousWeekEnd.setDate(previousWeekEnd.getDate() - 1);
        const { data: wStock = [] } = await supabase
          .from('sales')
          .select('total_price')
          .gte('sale_date', previousWeekStart.toISOString())
          .lte('sale_date', previousWeekEnd.toISOString());
        previousPeriodTotal = (wStock || []).reduce((sum, s) => sum + (parseFloat(s.total_price) || 0), 0);
      } else if (dateRange === 'month') {
        const previousMonth = new Date(selectedDate);
        previousMonth.setMonth(previousMonth.getMonth() - 1);
        const prevMonthStart = new Date(previousMonth.getFullYear(), previousMonth.getMonth(), 1);
        const prevMonthEnd = new Date(previousMonth.getFullYear(), previousMonth.getMonth() + 1, 0);
        const { data: mStock = [] } = await supabase
          .from('sales')
          .select('total_price')
          .gte('sale_date', prevMonthStart.toISOString())
          .lte('sale_date', prevMonthEnd.toISOString());
        previousPeriodTotal = (mStock || []).reduce((sum, s) => sum + (parseFloat(s.total_price) || 0), 0);
      }

      const growthRate = previousPeriodTotal > 0
        ? ((periodTotal - previousPeriodTotal) / previousPeriodTotal) * 100
        : 0;

      return {
        data: {
          periodSales: periodTotal,
          periodQuantity: periodQuantity,
          totalSales: totalSales || 0,
          previousPeriodSales: previousPeriodTotal,
          growthRate: growthRate,
          salesData: stockPeriod || []
        }
      };
    } catch (error) {
      console.error('Error fetching sales stats:', error);
      throw error;
    }
  },

  // Get sales history with filters
  getSalesHistory: async (params = {}) => {
    try {
      let query = supabase
        .from('sales')
        .select(`
          *,
          products (
            id,
            name,
            product_code,
            brand
          )
        `)
        .order('sale_date', { ascending: false });

      // Apply filters
      if (params.startDate) {
        query = query.gte('sale_date', params.startDate);
      }
      if (params.endDate) {
        query = query.lte('sale_date', params.endDate);
      }
      if (params.productId) {
        query = query.eq('product_id', params.productId);
      }
      if (params.customerId) {
        query = query.eq('customer_id', params.customerId);
      }

      // Pagination
      if (params.page && params.limit) {
        const from = (params.page - 1) * params.limit;
        const to = from + params.limit - 1;
        query = query.range(from, to);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      return {
        data: {
          sales: data || [],
          pagination: {
            total: count || 0,
            page: params.page || 1,
            limit: params.limit || 10,
            totalPages: Math.ceil((count || 0) / (params.limit || 10))
          }
        }
      };
    } catch (error) {
      console.error('Get sales history error:', error);
      throw error;
    }
  },

  // Get sales statistics (duplicate removed)

  // Get daily sales
  getDailySales: async (date) => {
    const startDate = new Date(date);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(date);
    endDate.setHours(23, 59, 59, 999);

    return salesAPI.getSalesHistory({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });
  },

  // Get weekly sales - 1 week back from selected date
  getWeeklySales: async (endDate) => {
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    const start = new Date(end);
    start.setDate(start.getDate() - 6); // 1 week back
    start.setHours(0, 0, 0, 0);

    return salesAPI.getSalesHistory({
      startDate: start.toISOString(),
      endDate: end.toISOString()
    });
  },

  // Get monthly sales - entire month of selected date
  getMonthlySales: async (date) => {
    const selectedDate = new Date(date);
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();

    const startDate = new Date(year, month, 1);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(year, month + 1, 0);
    endDate.setHours(23, 59, 59, 999);

    return salesAPI.getSalesHistory({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });
  },

  // Update sale
  updateSale: async (saleId, saleData) => {
    try {
      const { data, error } = await supabase
        .from('sales')
        .update({
          quantity: saleData.quantity,
          unit_price: saleData.unitPrice,
          total_price: saleData.totalPrice
        })
        .eq('id', saleId)
        .select()
        .single();

      if (error) throw error;

      // Update financial summary for the sale date (must be awaited)
      const saleDate = new Date(data.sale_date);
      const savePromise = dashboardAPI.saveFinancialSummary(saleDate);

      savePromise.catch(() => {
        // Silent fail - don't log errors
      });

      return { data };
    } catch (error) {
      console.error('Update sale error:', error);
      throw error;
    }
  },

  // Delete sale
  deleteSale: async (saleId, quantity, productId) => {
    try {
      // First get current stock and sale date
      const [{ data: product, error: productError }, { data: sale, error: saleError }] = await Promise.all([
        supabase
          .from('products')
          .select('stock')
          .eq('id', productId)
          .single(),
        supabase
          .from('sales')
          .select('sale_date')
          .eq('id', saleId)
          .single()
      ]);

      if (productError) throw productError;
      if (saleError) throw saleError;

      // Delete the sale
      const { error: deleteError } = await supabase
        .from('sales')
        .delete()
        .eq('id', saleId);

      if (deleteError) throw deleteError;

      // No stock movement record to delete for sales (sales are inherently stock outflows)

      // Restore stock
      const newStock = product.stock + quantity;
      const { error: stockError } = await supabase
        .from('products')
        .update({ stock: newStock })
        .eq('id', productId);

      if (stockError) throw stockError;

      // Update financial summary for the sale date (must be awaited)
      const saleDate = new Date(sale.sale_date);
      const savePromise = dashboardAPI.saveFinancialSummary(saleDate);

      savePromise.catch(() => {
        // Silent fail - don't log errors
      });

      return { data: { success: true } };
    } catch (error) {
      console.error('Delete sale error:', error);
      throw error;
    }
  }
};

// Car Sales API - sales performed from vehicles
export const carSalesAPI = {
  create: async (saleData) => {
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;

      const { data, error } = await supabase
        .from('car_sales')
        .insert([{
          car_id: saleData.carId,
          product_id: saleData.productId,
          quantity: saleData.quantity,
          unit_price: saleData.unitPrice,
          total_price: saleData.totalPrice,
          sale_date: new Date().toISOString(),
          notes: saleData.notes || null,
          created_by: currentUser?.id
        }])
        .select(`*, products(id, name, brand, product_code), cars(id, plate)`)
        .single();

      if (error) throw error;

      // No stock update for car sales - stock was already transferred to the car
      // Car sales only deduct from the car's inventory, not from main warehouse

      // Update financial summary for the sale date (must be awaited)
      const saleDate = new Date(data.sale_date);
      const savePromise = dashboardAPI.saveFinancialSummary(saleDate);

      savePromise.catch(() => {
        // Silent fail - don't log errors
      });

      return { data };
    } catch (error) {
      console.error('Create car sale error:', error);
      throw error;
    }
  },

  getByCarId: async (carId, params = {}) => {
    try {
      let query = supabase
        .from('car_sales')
        .select(`*, products(id, name, brand, product_code)`)
        .eq('car_id', carId)
        .order('sale_date', { ascending: false });

      if (params.startDate) {
        const startDate = new Date(params.startDate);
        startDate.setHours(0, 0, 0, 0);
        query = query.gte('sale_date', startDate.toISOString());
      }
      if (params.endDate) {
        const endDate = new Date(params.endDate);
        endDate.setHours(23, 59, 59, 999);
        query = query.lte('sale_date', endDate.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;

      const sales = (data || []).map(row => ({
        id: row.id,
        carId: row.car_id,
        productId: row.product_id,
        productName: row.products?.name,
        productBrand: row.products?.brand,
        productCode: row.products?.product_code,
        quantity: row.quantity,
        unitPrice: parseFloat(row.unit_price),
        totalPrice: parseFloat(row.total_price),
        saleDate: row.sale_date,
        notes: row.notes,
        createdAt: row.created_at,
        createdBy: row.created_by
      }));

      return { data: { sales } };
    } catch (error) {
      console.error('Get car sales error:', error);
      throw error;
    }
  },

  // Get car sales by product for stock movement summary
  getByProduct: async (productId, params = {}) => {
    try {
      let query = supabase
        .from('car_sales')
        .select(`*, products(id, name, brand, product_code), cars(id, plate)`)
        .eq('product_id', productId)
        .order('sale_date', { ascending: false });

      if (params.startDate) {
        query = query.gte('sale_date', params.startDate);
      }
      if (params.endDate) {
        query = query.lte('sale_date', params.endDate);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Transform to match stock sales format
      const sales = (data || []).map(row => ({
        id: row.id,
        car_id: row.car_id,
        carPlate: row.cars?.plate,
        product_id: row.product_id,
        quantity: row.quantity,
        unit_price: row.unit_price,
        unitPrice: parseFloat(row.unit_price),
        total_price: row.total_price,
        totalPrice: parseFloat(row.total_price),
        sale_date: row.sale_date,
        notes: row.notes,
        created_at: row.created_at,
        created_by: row.created_by
      }));

      return { data: sales };
    } catch (error) {
      console.error('Get car sales by product error:', error);
      throw error;
    }
  },

  // Delete a car sale
  deleteCarSale: async (carSaleId) => {
    try {
      // Get the car sale to get the sale date for financial summary update
      const { data: carSale, error: fetchError } = await supabase
        .from('car_sales')
        .select('sale_date')
        .eq('id', carSaleId)
        .single();

      if (fetchError) throw fetchError;

      // Delete the car sale record
      // Car stock is computed (transfers - sales), so deleting automatically restores car inventory
      const { error: deleteError } = await supabase
        .from('car_sales')
        .delete()
        .eq('id', carSaleId);

      if (deleteError) throw deleteError;

      // Update financial summary for the sale date
      const saleDate = new Date(carSale.sale_date);
      const savePromise = dashboardAPI.saveFinancialSummary(saleDate);

      savePromise.catch(() => {
        // Silent fail - don't log errors
      });

      return { data: { success: true } };
    } catch (error) {
      console.error('Delete car sale error:', error);
      throw error;
    }
  }
};

// Transaction Tracking API - Dynamic data fetching
export const transactionAPI = {
  // Get balance transactions (only paid ones) for a specific period
  getBalanceTransactions: async (params = {}) => {
    try {
      // Fetch balance transactions with customers only (avoid LEFT JOIN issue)
      let query = supabase
        .from('balance_transactions')
        .select(`
          *,
          customers(
            id,
            first_name,
            last_name
          )
        `)
        .eq('type', 'payment') // Only paid transactions
        .order('created_at', { ascending: false });

      // Apply date filters - use created_at for more accurate filtering
      if (params.startDate) {
        const startDate = new Date(params.startDate);
        startDate.setHours(0, 0, 0, 0);
        query = query.gte('created_at', startDate.toISOString());
      }
      if (params.endDate) {
        const endDate = new Date(params.endDate);
        endDate.setHours(23, 59, 59, 999);
        query = query.lte('created_at', endDate.toISOString());
      }

      // Apply search filter (simplified - only on direct fields and customers)
      if (params.search) {
        query = query.or(`description.ilike.%${params.search}%, customers.first_name.ilike.%${params.search}%, customers.last_name.ilike.%${params.search}%`);
      }

      // Pagination
      if (params.limit) {
        query = query.limit(params.limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Fetch product operation details separately if needed
      let productOperationsMap = {};
      if (data && data.length > 0) {
        const detailIds = data
          .filter(tx => tx.detail_id)
          .map(tx => tx.detail_id);

        if (detailIds.length > 0) {
          const { data: operationDetails } = await supabase
            .from('product_operation_details')
            .select(`
              id,
              product_operations(
                id,
                product_name,
                model,
                brand
              )
            `)
            .in('id', detailIds);

          if (operationDetails) {
            productOperationsMap = operationDetails.reduce((acc, detail) => {
              acc[detail.id] = detail;
              return acc;
            }, {});
          }
        }
      }

      // Transform data for frontend
      const transactions = data?.map(transaction => {
        const operationDetail = transaction.detail_id ? productOperationsMap[transaction.detail_id] : null;
        return {
          id: transaction.id,
          customerName: `${transaction.customers.first_name} ${transaction.customers.last_name}`.trim(),
          productName: operationDetail?.product_operations?.product_name || transaction.description,
          model: operationDetail?.product_operations?.model || '',
          brand: operationDetail?.product_operations?.brand || '',
          transactionType: transaction.description,
          amount: parseFloat(transaction.amount),
          date: transaction.transaction_date,
          isPayment: transaction.type === 'payment',
          notes: transaction.notes
        };
      }) || [];

      return { data: { transactions } };
    } catch (error) {
      console.error('Get balance transactions error:', error);
      throw error;
    }
  },

  // Get sales for a specific period
  getSales: async (params = {}) => {
    try {
      // Fetch both stock sales and car sales, then merge
      let stockSalesQuery = supabase
        .from('sales')
        .select(`
          *,
          products!inner(
            id,
            name,
            product_code,
            brand
          )
        `)
        .order('created_at', { ascending: false });

      let carSalesQuery = supabase
        .from('car_sales')
        .select(`
          *,
          products!inner(
            id,
            name,
            product_code,
            brand
          ),
          cars!inner(
            id,
            plate
          )
        `)
        .order('created_at', { ascending: false });

      // Apply date filters - use created_at for consistent filtering
      if (params.startDate) {
        const startDate = new Date(params.startDate);
        startDate.setHours(0, 0, 0, 0);
        stockSalesQuery = stockSalesQuery.gte('created_at', startDate.toISOString());
        carSalesQuery = carSalesQuery.gte('created_at', startDate.toISOString());
      }
      if (params.endDate) {
        const endDate = new Date(params.endDate);
        endDate.setHours(23, 59, 59, 999);
        stockSalesQuery = stockSalesQuery.lte('created_at', endDate.toISOString());
        carSalesQuery = carSalesQuery.lte('created_at', endDate.toISOString());
      }

      // Apply search filter
      if (params.search) {
        stockSalesQuery = stockSalesQuery.or(`products.name.ilike.%${params.search}%, products.brand.ilike.%${params.search}%`);
        carSalesQuery = carSalesQuery.or(`products.name.ilike.%${params.search}%, products.brand.ilike.%${params.search}%, cars.plate.ilike.%${params.search}%`);
      }

      // Pagination
      if (params.limit) {
        stockSalesQuery = stockSalesQuery.limit(params.limit);
        carSalesQuery = carSalesQuery.limit(params.limit);
      }

      const [stockRes, carRes] = await Promise.all([stockSalesQuery, carSalesQuery]);
      const { data: stockData, error: stockError } = stockRes;
      const { data: carData, error: carError } = carRes;

      if (stockError) throw stockError;
      if (carError) throw carError;

      // Transform and merge
      const stockSales = (stockData || []).map(sale => ({
        id: sale.id,
        type: 'stock',
        customerName: 'Stok Satışı',
        productName: sale.products.name,
        salesId: `S${sale.sale_date.split('T')[0].replace(/-/g, '')}-${String(sale.id).substring(0, 8).toUpperCase()}`,
        amount: parseFloat(sale.total_price),
        date: sale.sale_date,
        quantity: sale.quantity,
        unitPrice: parseFloat(sale.unit_price),
        productCode: sale.products.product_code,
        brand: sale.products.brand
      }));

      const carSales = (carData || []).map(sale => ({
        id: sale.id,
        type: 'car',
        customerName: `Araç Satışı - ${sale.cars.plate}`,
        productName: sale.products.name,
        salesId: `C${sale.sale_date.split('T')[0].replace(/-/g, '')}-${String(sale.id).substring(0, 8).toUpperCase()}`,
        amount: parseFloat(sale.total_price),
        date: sale.sale_date,
        quantity: sale.quantity,
        unitPrice: parseFloat(sale.unit_price),
        productCode: sale.products.product_code,
        brand: sale.products.brand,
        carPlate: sale.cars.plate
      }));

      const sales = [...stockSales, ...carSales]
        .sort((a, b) => new Date(b.date) - new Date(a.date));

      return { data: { sales } };
    } catch (error) {
      console.error('Get sales error:', error);
      throw error;
    }
  },

  // Get daily data
  getDailyData: async (date, searchParams = {}) => {
    const startDate = new Date(date);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(date);
    endDate.setHours(23, 59, 59, 999);

    const [balanceResponse, salesResponse] = await Promise.all([
      transactionAPI.getBalanceTransactions({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        search: searchParams.customerSearch || '',
        limit: 50
      }),
      transactionAPI.getSales({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        search: searchParams.productSearch || '',
        limit: 50
      })
    ]);

    return {
      balanceTransactions: balanceResponse.data.transactions,
      sales: salesResponse.data.sales
    };
  },

  // Get weekly data
  getWeeklyData: async (endDate, searchParams = {}) => {
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    const start = new Date(end);
    start.setDate(start.getDate() - 6);
    start.setHours(0, 0, 0, 0);

    const [balanceResponse, salesResponse] = await Promise.all([
      transactionAPI.getBalanceTransactions({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        search: searchParams.customerSearch || '',
        limit: 100
      }),
      transactionAPI.getSales({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        search: searchParams.productSearch || '',
        limit: 100
      })
    ]);

    return {
      balanceTransactions: balanceResponse.data.transactions,
      sales: salesResponse.data.sales
    };
  },

  // Get monthly data
  getMonthlyData: async (date, searchParams = {}) => {
    const selectedDate = new Date(date);
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();

    const startDate = new Date(year, month, 1);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(year, month + 1, 0);
    endDate.setHours(23, 59, 59, 999);

    const [balanceResponse, salesResponse] = await Promise.all([
      transactionAPI.getBalanceTransactions({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        search: searchParams.customerSearch || '',
        limit: 200
      }),
      transactionAPI.getSales({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        search: searchParams.productSearch || '',
        limit: 200
      })
    ]);

    return {
      balanceTransactions: balanceResponse.data.transactions,
      sales: salesResponse.data.sales
    };
  },

  // Get car transfers for a specific period
  getCarTransfers: async (params = {}) => {
    try {
      let query = supabase
        .from('car_transfers')
        .select(`
          *,
          cars!inner(
            id,
            plate
          ),
          products!inner(
            id,
            name,
            brand,
            product_code
          )
        `)
        .order('created_at', { ascending: false });

      // Apply date filters
      if (params.startDate) {
        const startDate = new Date(params.startDate);
        startDate.setHours(0, 0, 0, 0);
        query = query.gte('created_at', startDate.toISOString());
      }
      if (params.endDate) {
        const endDate = new Date(params.endDate);
        endDate.setHours(23, 59, 59, 999);
        query = query.lte('created_at', endDate.toISOString());
      }

      // Pagination
      if (params.limit) {
        query = query.limit(params.limit);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Transform data for frontend
      const transfers = data?.map(transfer => ({
        id: transfer.id,
        carPlate: transfer.cars.plate,
        productName: transfer.products.name,
        productBrand: transfer.products.brand,
        productCode: transfer.products.product_code,
        quantity: transfer.quantity,
        transferDate: transfer.transfer_date,
        notes: transfer.notes,
        createdAt: transfer.created_at
      })) || [];

      return { data: { transfers } };
    } catch (error) {
      console.error('Get car transfers error:', error);
      throw error;
    }
  },

  // Get summary statistics
  getSummaryStats: async (params = {}) => {
    try {
      const [balanceResponse, salesResponse] = await Promise.all([
        transactionAPI.getBalanceTransactions(params),
        transactionAPI.getSales(params)
      ]);

      const payments = balanceResponse.data.transactions;
      const sales = salesResponse.data.sales;

      const paymentsTotal = payments.reduce((sum, transaction) => sum + transaction.amount, 0);
      const salesTotal = sales.reduce((sum, sale) => sum + sale.amount, 0);

      return {
        payments: { count: payments.length, total: paymentsTotal },
        sales: { count: sales.length, total: salesTotal }
      };
    } catch (error) {
      console.error('Get summary stats error:', error);
      throw error;
    }
  }
};

// Cars API
export const carsAPI = {
  // Get all cars with product details
  getAll: async (params = {}) => {
    try {
      let query = supabase
        .from('cars')
        .select(`
          *,
          products(
            id,
            name,
            brand,
            product_code
          )
        `)
        .order('created_at', { ascending: false });

      // Apply search filter
      if (params.search) {
        query = query.or(`plate.ilike.%${params.search}%, products.name.ilike.%${params.search}%, products.brand.ilike.%${params.search}%`);
      }

      // Apply pagination
      if (params.limit) {
        query = query.limit(params.limit);
      }
      if (params.offset) {
        query = query.range(params.offset, params.offset + (params.limit || 10) - 1);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Get cars error:', error);
        throw error;
      }

      // Transform data for frontend
      const cars = data?.map(car => ({
        id: car.id,
        plate: car.plate,
        productId: car.product_id,
        productName: car.products?.name || 'Ürün atanmamış',
        productBrand: car.products?.brand || '',
        productCode: car.products?.product_code || '',
        createdAt: car.created_at,
        createdBy: car.created_by
      })) || [];

      return { data: { cars } };
    } catch (error) {
      console.error('Get cars error:', error);
      throw error;
    }
  },

  // Create a new car
  create: async (carData) => {
    try {
      const insertData = {
        plate: carData.plate.toUpperCase(),
        created_by: carData.createdBy
      };

      // Only add product_id if it's not null
      if (carData.productId) {
        insertData.product_id = carData.productId;
      }

      const { data, error } = await supabase
        .from('cars')
        .insert([insertData])
        .select(`
          *,
          products(
            id,
            name,
            brand,
            product_code
          )
        `)
        .single();

      if (error) {
        console.error('Create car error:', error);
        throw error;
      }

      // Transform data for frontend
      const car = {
        id: data.id,
        plate: data.plate,
        productId: data.product_id,
        productName: data.products?.name || 'Ürün atanmamış',
        productBrand: data.products?.brand || '',
        productCode: data.products?.product_code || '',
        createdAt: data.created_at,
        createdBy: data.created_by
      };

      return { data: { car } };
    } catch (error) {
      console.error('Create car error:', error);
      throw error;
    }
  },

  // Update a car
  update: async (carId, carData) => {
    try {
      const { data, error } = await supabase
        .from('cars')
        .update({
          plate: carData.plate?.toUpperCase(),
          updated_by: carData.updatedBy
        })
        .eq('id', carId)
        .select(`
          *,
          products(
            id,
            name,
            brand,
            product_code
          )
        `)
        .single();

      if (error) {
        console.error('Update car error:', error);
        throw error;
      }

      // Transform data for frontend
      const car = {
        id: data.id,
        plate: data.plate,
        productId: data.product_id,
        productName: data.products?.name || 'Ürün atanmamış',
        productBrand: data.products?.brand || '',
        productCode: data.products?.product_code || '',
        createdAt: data.created_at,
        createdBy: data.created_by
      };

      return { data: { car } };
    } catch (error) {
      console.error('Update car error:', error);
      throw error;
    }
  },

  // Delete a car
  delete: async (carId) => {
    try {
      const { error } = await supabase
        .from('cars')
        .delete()
        .eq('id', carId);

      if (error) {
        console.error('Delete car error:', error);
        throw error;
      }

      return { data: { success: true } };
    } catch (error) {
      console.error('Delete car error:', error);
      throw error;
    }
  }
};

// Car Transfers API
export const carTransfersAPI = {
  // Get transfers for a specific car
  getByCarId: async (carId, params = {}) => {
    try {
      let query = supabase
        .from('car_transfers')
        .select(`
          *,
          products(
            id,
            name,
            brand,
            product_code
          )
        `)
        .eq('car_id', carId)
        .order('transfer_date', { ascending: false });

      // Apply search filter
      if (params.search) {
        query = query.or(`products.name.ilike.%${params.search}%, products.brand.ilike.%${params.search}%`);
      }

      // Apply date filters
      if (params.startDate) {
        const startDate = new Date(params.startDate);
        startDate.setHours(0, 0, 0, 0);
        query = query.gte('transfer_date', startDate.toISOString());
      }
      if (params.endDate) {
        const endDate = new Date(params.endDate);
        endDate.setHours(23, 59, 59, 999);
        query = query.lte('transfer_date', endDate.toISOString());
      }

      const { data, error } = await query;

      if (error) {
        console.error('Get car transfers error:', error);
        throw error;
      }

      // Transform data for frontend
      const transfers = data?.map(transfer => ({
        id: transfer.id,
        carId: transfer.car_id,
        productId: transfer.product_id,
        productName: transfer.products.name,
        productBrand: transfer.products.brand,
        productModel: transfer.products.model,
        productCode: transfer.products.product_code,
        quantity: transfer.quantity,
        transferDate: transfer.transfer_date,
        notes: transfer.notes,
        createdAt: transfer.created_at,
        createdBy: transfer.created_by
      })) || [];

      return { data: { transfers } };
    } catch (error) {
      console.error('Get car transfers error:', error);
      throw error;
    }
  },

  // Create a new transfer
  create: async (transferData) => {
    try {
      const { data, error } = await supabase
        .from('car_transfers')
        .insert([{
          car_id: transferData.carId,
          product_id: transferData.productId,
          quantity: transferData.quantity,
          notes: transferData.notes,
          transfer_date: new Date().toISOString(),
          created_by: transferData.createdBy
        }])
        .select(`
          *,
          products(
            id,
            name,
            brand,
            product_code
          )
        `)
        .single();

      if (error) {
        console.error('Create car transfer error:', error);
        throw error;
      }

      // Transform data for frontend
      const transfer = {
        id: data.id,
        carId: data.car_id,
        productId: data.product_id,
        productName: data.products.name,
        productBrand: data.products.brand,
        productModel: data.products.model,
        productCode: data.products.product_code,
        quantity: data.quantity,
        transferDate: data.transfer_date,
        notes: data.notes,
        createdAt: data.created_at,
        createdBy: data.created_by
      };

      return { data: { transfer } };
    } catch (error) {
      console.error('Create car transfer error:', error);
      throw error;
    }
  },

  // Delete a transfer
  delete: async (transferId) => {
    try {
      const { error } = await supabase
        .from('car_transfers')
        .delete()
        .eq('id', transferId);

      if (error) {
        console.error('Delete car transfer error:', error);
        throw error;
      }

      return { data: { success: true } };
    } catch (error) {
      console.error('Delete car transfer error:', error);
      throw error;
    }
  },

  // Get aggregated stock of a product across cars
  getProductStocks: async (productId) => {
    try {
      if (!productId) {
        throw new Error('Product ID is required');
      }

      const { data: transferRows, error: transferError } = await supabase
        .from('car_transfers')
        .select(`
          id,
          car_id,
          quantity,
          cars!inner(
            id,
            plate
          )
        `)
        .eq('product_id', productId);

      if (transferError) throw transferError;

      const { data: salesRows, error: salesError } = await supabase
        .from('car_sales')
        .select('car_id, quantity')
        .eq('product_id', productId);

      if (salesError) throw salesError;

      const carMap = new Map();

      (transferRows || []).forEach(row => {
        const carId = row.car_id;
        if (!carMap.has(carId)) {
          carMap.set(carId, {
            carId,
            plate: row.cars?.plate || 'Bilinmeyen',
            quantity: 0
          });
        }
        const entry = carMap.get(carId);
        entry.quantity += parseFloat(row.quantity) || 0;
      });

      (salesRows || []).forEach(row => {
        const carId = row.car_id;
        if (!carMap.has(carId)) {
          carMap.set(carId, {
            carId,
            plate: 'Bilinmeyen',
            quantity: 0
          });
        }
        const entry = carMap.get(carId);
        entry.quantity -= parseFloat(row.quantity) || 0;
      });

      const stocks = Array.from(carMap.values())
        .filter(entry => (entry.quantity || 0) > 0)
        .map(entry => ({
          ...entry,
          carId: String(entry.carId),
          quantity: Math.max(0, entry.quantity)
        }));

      const total = stocks.reduce((sum, entry) => sum + entry.quantity, 0);

      return { data: { stocks, total } };
    } catch (error) {
      console.error('Get product stocks in cars error:', error);
      throw error;
    }
  },

  // Transfer product quantity from a car back to main stock
  returnToStock: async ({ carId, productId, quantity, notes = '', createdBy = null }) => {
    try {
      const amount = parseInt(quantity, 10);

      if (!carId || !productId) {
        throw new Error('Araç ve ürün bilgileri gerekli');
      }

      if (!Number.isFinite(amount) || amount <= 0) {
        throw new Error('Geçerli bir miktar girin');
      }

      const { data: transfers, error: transfersError } = await supabase
        .from('car_transfers')
        .select('quantity')
        .eq('car_id', carId)
        .eq('product_id', productId);

      if (transfersError) throw transfersError;

      const { data: sales, error: salesError } = await supabase
        .from('car_sales')
        .select('quantity')
        .eq('car_id', carId)
        .eq('product_id', productId);

      if (salesError) throw salesError;

      const totalTransfers = (transfers || []).reduce((sum, row) => sum + (parseFloat(row.quantity) || 0), 0);
      const totalSales = (sales || []).reduce((sum, row) => sum + (parseFloat(row.quantity) || 0), 0);
      const available = totalTransfers - totalSales;

      if (amount > available) {
        throw new Error(`Araçta yeterli stok yok. Mevcut transfer edilebilir miktar: ${available}`);
      }

      const { data, error } = await supabase
        .from('car_transfers')
        .insert([{
          car_id: carId,
          product_id: productId,
          quantity: -Math.abs(amount),
          notes: notes || 'Araçtan geri transfer',
          transfer_date: new Date().toISOString(),
          created_by: createdBy
        }])
        .select(`
          *,
          cars(id, plate),
          products(id, name, brand, product_code)
        `)
        .single();

      if (error) throw error;

      try {
        await productsAPI.updateStock(productId, amount, {
          movementType: 'transfer_in',
          referenceType: 'car_transfer_return',
          referenceId: data.id,
          notes: `Araçtan geri transfer - ${data.cars?.plate || ''}`,
          createdBy
        });
      } catch (stockError) {
        await supabase
          .from('car_transfers')
          .delete()
          .eq('id', data.id);
        throw stockError;
      }

      return { data };
    } catch (error) {
      console.error('Return to stock from car error:', error);
      throw error;
    }
  }
};

// Dashboard API - Admin summary dashboard functions
export const dashboardAPI = {
  // Get dashboard summary data for admin
  getDashboardSummary: async (params = {}) => {
    try {
      const { startDate: providedStartDate, endDate: providedEndDate, dateRange = 'today' } = params;

      // Use provided date range or calculate based on filter
      let startDate, endDate;

      if (providedStartDate && providedEndDate) {
        startDate = new Date(providedStartDate);
        endDate = new Date(providedEndDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
      } else {
        // Default to today if no dates provided
        startDate = new Date();
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        endDate.setHours(23, 59, 59, 999);
      }

      const startTime = startDate.getTime();
      const endTime = endDate.getTime();

      const parseToDate = (value) => {
        if (!value) return null;
        if (value instanceof Date) return value;
        if (typeof value === 'string') {
          if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return new Date(`${value}T00:00:00`);
          }
          const parsed = new Date(value);
          return Number.isNaN(parsed.getTime()) ? null : parsed;
        }
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? null : parsed;
      };

      const isWithinRange = (value) => {
        const parsed = parseToDate(value);
        if (!parsed) return false;
        const time = parsed.getTime();
        return time >= startTime && time <= endTime;
      };

      // Get service transactions - Use local date format to avoid UTC issues
      const serviceStartDateStr = startDate.getFullYear() + '-' +
        String(startDate.getMonth() + 1).padStart(2, '0') + '-' +
        String(startDate.getDate()).padStart(2, '0');
      const serviceEndDateStr = endDate.getFullYear() + '-' +
        String(endDate.getMonth() + 1).padStart(2, '0') + '-' +
        String(endDate.getDate()).padStart(2, '0');

      const { data: serviceData, error: serviceError } = await supabase
        .from('service_transactions')
        .select('*')
        .gte('date', serviceStartDateStr)
        .lte('date', serviceEndDateStr)
        .order('date', { ascending: false });

      if (serviceError) throw serviceError;

      // Database query already filtered by date - no need for isWithinRange
      const filteredServiceTransactions = serviceData || [];
      const serviceIncomeTransactions = filteredServiceTransactions.filter(transaction => transaction.type === 'income');
      const serviceExpenseTransactions = filteredServiceTransactions.filter(transaction => transaction.type === 'expense');

      // Get daily expenses - Use local date format to avoid UTC issues
      const startDateStr = startDate.getFullYear() + '-' +
        String(startDate.getMonth() + 1).padStart(2, '0') + '-' +
        String(startDate.getDate()).padStart(2, '0');
      const endDateStr = endDate.getFullYear() + '-' +
        String(endDate.getMonth() + 1).padStart(2, '0') + '-' +
        String(endDate.getDate()).padStart(2, '0');

      const { data: expenses, error: expensesError } = await supabase
        .from('daily_expenses')
        .select('*')
        .gte('date', startDateStr)
        .lte('date', endDateStr)
        .order('date', { ascending: false });

      if (expensesError) throw expensesError;

      // (Removed: products profit prefetch not needed for this card set)

      // Note: Database query already filters by date range
      const filteredExpenseRows = (expenses || []) || [];

      // Calculate expenses from daily_expenses (used as cash expenses)
      const totalExpensesFromExpenses = filteredExpenseRows.reduce(
        (sum, expense) => sum + parseFloat(expense.amount || 0),
        0
      );

      // Calculate product profits based on actual sales (stock + car sales)
      // Fetch stock sales and car sales for the period and merge
      const [stockSalesRes, carSalesRes] = await Promise.all([
        supabase
          .from('sales')
          .select(`
            *,
            products (
              id,
              name,
              cost,
              price
            )
          `)
          .gte('sale_date', startDate.toISOString())
          .lte('sale_date', endDate.toISOString()),
        supabase
          .from('car_sales')
          .select(`
            *,
            products (
              id,
              name,
              cost,
              price
            ),
            cars (
              id,
              plate
            )
          `)
          .gte('sale_date', startDate.toISOString())
          .lte('sale_date', endDate.toISOString())
      ]);

      const stockSalesData = stockSalesRes?.data || [];
      const carSalesData = carSalesRes?.data || [];
      if (stockSalesRes?.error) {
        console.error('Error fetching stock sales data:', stockSalesRes.error);
      }
      if (carSalesRes?.error) {
        console.error('Error fetching car sales data:', carSalesRes.error);
      }
      const filteredStockSalesData = stockSalesData.filter(sale =>
        isWithinRange(sale?.sale_date || sale?.created_at)
      );
      const filteredCarSalesData = carSalesData.filter(sale =>
        isWithinRange(sale?.sale_date || sale?.created_at)
      );
      const salesData = [...filteredStockSalesData, ...filteredCarSalesData];

      // Get current stock information from products table
      const uniqueProductIds = [...new Set(salesData.map(sale => sale.product_id))];
      const { data: productsData } = await supabase
        .from('products')
        .select('id, name, brand, stock, price, cost')
        .in('id', uniqueProductIds);

      const productsMap = {};
      (productsData || []).forEach(product => {
        productsMap[product.id] = product;
      });

      // Calculate actual profits from sales
      const productProfits = salesData?.map(sale => {
        const product = sale.products;
        const currentProduct = productsMap[sale.product_id];

        if (!product) return null;

        const costValue = product.cost !== undefined && product.cost !== null ? product.cost : currentProduct?.cost;
        const priceValue = product.price !== undefined && product.price !== null ? product.price : currentProduct?.price;

        if (costValue === undefined || costValue === null || priceValue === undefined || priceValue === null) {
          return null;
        }

        const cost = parseFloat(costValue);
        const price = parseFloat(priceValue);

        const unitProfit = price - cost;
        const totalProfit = unitProfit * sale.quantity;
        const profitMargin = cost > 0 ? (unitProfit / cost) * 100 : 0;

        return {
          id: product.id,
          name: product.name,
          brand: product.brand || '',
          profit: totalProfit,
          profitMargin: profitMargin,
          quantity: sale.quantity,
          stock: currentProduct?.stock || 0,
          unitPrice: price,
          unitCost: cost
        };
      }).filter(product => product !== null).sort((a, b) => b.profit - a.profit) || [];

      const totalProfit = productProfits.reduce((sum, product) => sum + product.profit, 0);
      const averageProfitMargin = productProfits.length > 0 && totalProfit > 0
        ? productProfits.reduce((sum, product) => sum + product.profitMargin, 0) / productProfits.length
        : 0;

      // Sales total for selected period
      // Split sales income: stock vs car
      const stockSalesIncomeTotal = filteredStockSalesData.reduce(
        (sum, sale) => sum + parseFloat(sale.total_price || 0),
        0
      );
      const carSalesIncomeTotal = filteredCarSalesData.reduce(
        (sum, sale) => sum + parseFloat(sale.total_price || 0),
        0
      );

      // Aggregate sales totals for cards: only within selected range
      const totalSalesAmount = (salesData || []).reduce((sum, sale) => sum + parseFloat(sale.total_price || 0), 0);

      // Aggregate total customer balance (all active customers)
      const { data: customersForBalance } = await supabase
        .from('customers')
        .select('balance')
        .eq('is_active', true);
      const totalCustomerBalance = (customersForBalance || []).reduce((s, c) => s + parseFloat(c.balance || 0), 0);

      // Fetch detailed payments from balance_transactions for transactions list
      // Split the query to avoid LEFT JOIN issues with product_operation_details
      const { data: paymentsData, error: paymentsError } = await supabase
        .from('balance_transactions')
        .select(`
          *,
          customers(
            id,
            first_name,
            last_name,
            phone
          )
        `)
        .gte('transaction_date', startDate.toISOString().split('T')[0])
        .lte('transaction_date', endDate.toISOString().split('T')[0])
        .or('type.eq.payment,amount.gt.0')
        .order('transaction_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (paymentsError) {
        console.error('Error fetching payments for dashboard transactions:', paymentsError);
      }

      // Fetch product operation details separately if needed
      let productOperationsMap = {};
      const paymentsRows = paymentsData || [];
      const filteredPaymentsData = paymentsRows.filter(tx =>
        isWithinRange(tx?.transaction_date) || isWithinRange(tx?.created_at)
      );

      if (filteredPaymentsData.length > 0) {
        const detailIds = filteredPaymentsData
          .filter(tx => tx.detail_id)
          .map(tx => tx.detail_id);

        if (detailIds.length > 0) {
          const { data: operationDetails } = await supabase
            .from('product_operation_details')
            .select(`
              id,
              product_operations(
                id,
                product_name,
                model,
                brand
              )
            `)
            .in('id', detailIds);

          if (operationDetails) {
            productOperationsMap = operationDetails.reduce((acc, detail) => {
              acc[detail.id] = detail;
              return acc;
            }, {});
          }
        }
      }

      const paymentTransactions = filteredPaymentsData.map(tx => {
        const operationDetail = tx.detail_id ? productOperationsMap[tx.detail_id] : null;
        return {
          id: tx.id,
          customerName: `${tx.customers?.first_name || ''} ${tx.customers?.last_name || ''}`.trim(),
          phone: tx.customers?.phone || '',
          productName: operationDetail?.product_operations?.product_name || tx.description,
          model: operationDetail?.product_operations?.model || '',
          brand: operationDetail?.product_operations?.brand || '',
          transactionType: tx.description,
          amount: parseFloat(tx.amount),
          date: tx.transaction_date || tx.created_at,
          isPayment: (tx.type === 'payment') || parseFloat(tx.amount) > 0,
          notes: tx.notes
        };
      }).sort((a, b) => {
        const dateA = parseToDate(a.date);
        const dateB = parseToDate(b.date);
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateB.getTime() - dateA.getTime();
      });

      // Build car sales transactions list (for separate car sales section)
      const carSalesTransactions = filteredCarSalesData.map(sale => ({
        id: sale.id,
        carPlate: sale.cars?.plate || '',
        productName: sale.products?.name || '',
        brand: sale.products?.brand || '',
        quantity: sale.quantity,
        amount: parseFloat(sale.total_price || 0),
        date: sale.sale_date || sale.created_at
      }));

      // Totals aligned with transaction list
      const paymentsTotal = paymentTransactions
        .filter(t => t.isPayment)
        .reduce((sum, t) => sum + (t.amount > 0 ? t.amount : 0), 0);
      const cashExpensesTotal = totalExpensesFromExpenses; // from daily_expenses

      const totalServiceIncome2 = serviceIncomeTransactions.reduce(
        (sum, transaction) => sum + parseFloat(transaction.amount || 0),
        0
      );
      const totalServiceExpenses2 = serviceExpenseTransactions.reduce(
        (sum, transaction) => sum + parseFloat(transaction.amount || 0),
        0
      );

      // Get manual cash income from daily_revenue_manual table
      const { data: manualCashIncomeData, error: manualCashIncomeError } = await supabase
        .from('daily_revenue_manual')
        .select('*')
        .gte('date', serviceStartDateStr)
        .lte('date', serviceEndDateStr)
        .eq('type', 'income');

      if (manualCashIncomeError) {
        console.error('Error fetching manual cash income:', manualCashIncomeError);
      }

      // Get manual cash expenses from daily_revenue_manual table
      const { data: manualCashExpenseData, error: manualCashExpenseError } = await supabase
        .from('daily_revenue_manual')
        .select('*')
        .gte('date', serviceStartDateStr)
        .lte('date', serviceEndDateStr)
        .eq('type', 'expense');

      if (manualCashExpenseError) {
        console.error('Error fetching manual cash expenses:', manualCashExpenseError);
      }

      // Calculate totals from manual cash operations
      const manualCashIncomeTotal = (manualCashIncomeData || []).reduce(
        (sum, item) => sum + parseFloat(item.amount || 0),
        0
      );
      const manualCashExpenseTotal = (manualCashExpenseData || []).reduce(
        (sum, item) => sum + parseFloat(item.amount || 0),
        0
      );

      // Nakit Akışı Yönetimi: satış geliri = stok + araç satışları (birlikte)
      const combinedSalesIncomeTotal = stockSalesIncomeTotal + carSalesIncomeTotal;
      const totalRevenue = paymentsTotal + combinedSalesIncomeTotal + totalServiceIncome2 + manualCashIncomeTotal;
      const totalExpenses = cashExpensesTotal + totalServiceExpenses2 + manualCashExpenseTotal;
      const netFlow2 = totalRevenue - totalExpenses;
      return {
        data: {
          dailyCashFlow: {
            income: totalRevenue,
            expenses: totalExpenses,
            netFlow: netFlow2,
            transactions: paymentTransactions,
            // Breakdown of income sources
            cashIncome: paymentsTotal + combinedSalesIncomeTotal + manualCashIncomeTotal,
            cashSalesIncome: combinedSalesIncomeTotal, // stock + car sales
            cashPaymentsIncome: paymentsTotal,
            serviceIncome: totalServiceIncome2,
            manualCashIncome: manualCashIncomeTotal,
            cashExpenses: cashExpensesTotal + manualCashExpenseTotal,
            manualCashExpenses: manualCashExpenseTotal,
            serviceExpenses: totalServiceExpenses2
          },
          carSalesFlow: {
            income: carSalesIncomeTotal,
            transactions: carSalesTransactions
          },
          productProfits: {
            totalProfit: totalProfit,
            profitMargin: averageProfitMargin,
            products: productProfits
          },
          salesSummary: {
            totalSalesAmount,
            totalCustomerBalance
          },
          revenue: {
            daily: totalRevenue,
            weekly: dateRange === 'week' ? totalRevenue : totalRevenue * 7,
            monthly: dateRange === 'month' ? totalRevenue : totalRevenue * 30,
            growth: 0 // TODO: Calculate growth compared to previous period
          },
          expenses: {
            daily: totalExpenses,
            weekly: dateRange === 'week' ? totalExpenses : totalExpenses * 7,
            monthly: dateRange === 'month' ? totalExpenses : totalExpenses * 30,
            categories: []
          },
          systemFlows: {
            sales: [], // TODO: Get actual sales data
            payments: [], // TODO: Get actual payments data
            transfers: [], // TODO: Get actual transfers data
            returns: [] // TODO: Get actual returns data
          },
          serviceData: {
            income: serviceIncomeTransactions,
            expenses: serviceExpenseTransactions
          },
          expenseData: filteredExpenseRows
        }
      };
    } catch (error) {
      console.error('Get dashboard summary error:', error);
      throw error;
    }
  },

  // Dynamically save financial summary for a specific date (Fast SQL method)
  saveFinancialSummary: async (date) => {
    try {
      const targetDate = date instanceof Date ? date : new Date(date);
      const dateStr = targetDate.toISOString().split('T')[0];

      // Calculate financial summary directly from tables
      const startDate = new Date(targetDate);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(targetDate);
      endDate.setHours(23, 59, 59, 999);

      // Get all financial data for the date in parallel
      const [
        { data: stockSalesData },
        { data: carSalesData },
        { data: serviceData },
        { data: balanceData },
        { data: expensesData },
        { data: manualRevenueData }
      ] = await Promise.all([
        // Stock sales
        supabase
          .from('sales')
          .select('total_price')
          .gte('sale_date', startDate.toISOString())
          .lte('sale_date', endDate.toISOString()),
        // Car sales
        supabase
          .from('car_sales')
          .select('total_price')
          .gte('sale_date', startDate.toISOString())
          .lte('sale_date', endDate.toISOString()),
        // Service transactions
        supabase
          .from('service_transactions')
          .select('type, amount')
          .gte('date', dateStr)
          .lte('date', dateStr),
        // Balance transactions (customer payments)
        supabase
          .from('balance_transactions')
          .select('type, amount')
          .gte('transaction_date', dateStr)
          .lte('transaction_date', dateStr),
        // Daily expenses
        supabase
          .from('daily_expenses')
          .select('amount')
          .gte('date', dateStr)
          .lte('date', dateStr),
        // Manual cash income from daily_revenue_manual
        supabase
          .from('daily_revenue_manual')
          .select('amount')
          .gte('date', dateStr)
          .lte('date', dateStr)
      ]);

      // Calculate total income
      const stockSalesIncome = (stockSalesData || []).reduce((sum, sale) => sum + parseFloat(sale.total_price || 0), 0);
      const carSalesIncome = (carSalesData || []).reduce((sum, sale) => sum + parseFloat(sale.total_price || 0), 0);
      const serviceIncome = (serviceData || [])
        .filter(t => t.type === 'income')
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
      const customerPaymentsIncome = (balanceData || [])
        .filter(t => t.type === 'payment' || parseFloat(t.amount || 0) > 0)
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
      const manualCashIncome = (manualRevenueData || [])
        .reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);

      const totalIncome = stockSalesIncome + carSalesIncome + serviceIncome + customerPaymentsIncome + manualCashIncome;

      // Calculate total expense
      const serviceExpense = (serviceData || [])
        .filter(t => t.type === 'expense')
        .reduce((sum, t) => sum + parseFloat(t.amount || 0), 0);
      const totalExpense = serviceExpense + (expensesData || []).reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

      const netAmount = totalIncome - totalExpense;

      // Save to daily_financial_summary table
      const { data, error } = await supabase
        .from('daily_financial_summary')
        .upsert({
          date: dateStr,
          total_income: totalIncome,
          total_expense: totalExpense,
          net_amount: netAmount,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'date'
        })
        .select()
        .single();

      if (error) throw error;

      return { data, success: true };
    } catch (error) {
      console.error('Save financial summary error:', error);
      // Don't throw - silent fail is better for user experience
      return { success: false, error };
    }
  },

  // Legacy function name for backward compatibility
  triggerDailySummarySave: async (date) => {
    return await this.saveFinancialSummary(date);
  },

  // Add service transaction
  addServiceTransaction: async (transactionData) => {
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      const { data, error } = await supabase
        .from('service_transactions')
        .insert([{
          date: transactionData.date,
          type: transactionData.type,
          name: transactionData.name,
          amount: transactionData.amount,
          description: transactionData.description,
          created_by: currentUser?.id
        }])
        .select()
        .single();

      if (error) throw error;

      // Update financial summary for the date
      try {
        await dashboardAPI.saveFinancialSummary(transactionData.date);
      } catch (saveError) {
        console.error('Failed to save financial summary:', saveError);
        // Don't throw - transaction was successful
      }

      return { data };
    } catch (error) {
      console.error('Add service transaction error:', error);
      throw error;
    }
  },

  // Update service transaction
  updateServiceTransaction: async (transactionId, transactionData) => {
    try {
      const { data, error } = await supabase
        .from('service_transactions')
        .update({
          date: transactionData.date,
          type: transactionData.type,
          name: transactionData.name,
          amount: transactionData.amount,
          description: transactionData.description
        })
        .eq('id', transactionId)
        .select()
        .single();

      if (error) throw error;

      // Update financial summary for the date
      try {
        await dashboardAPI.saveFinancialSummary(transactionData.date);
      } catch (saveError) {
        console.error('Failed to save financial summary:', saveError);
      }

      return { data };
    } catch (error) {
      console.error('Update service transaction error:', error);
      throw error;
    }
  },

  // Delete service transaction
  deleteServiceTransaction: async (transactionId) => {
    try {
      // Get transaction data first to know the date
      const { data: transaction, error: fetchError } = await supabase
        .from('service_transactions')
        .select('date')
        .eq('id', transactionId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('service_transactions')
        .delete()
        .eq('id', transactionId);

      if (error) throw error;

      // Update financial summary for the date
      try {
        await dashboardAPI.saveFinancialSummary(transaction.date);
      } catch (saveError) {
        console.error('Failed to save financial summary:', saveError);
      }

      return { data: { success: true } };
    } catch (error) {
      console.error('Delete service transaction error:', error);
      throw error;
    }
  },

  // Save service data (income and expenses)
  saveServiceData: async (saveData) => {
    try {

      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;
      if (!currentUser?.id) throw new Error('User not authenticated');

      const { income, expenses, removed, date } = saveData;

      if (!date) throw new Error('Date is required');

      // Delete removed transactions
      if (removed && removed.length > 0) {
        const { error: deleteError } = await supabase
          .from('service_transactions')
          .delete()
          .in('id', removed);

        if (deleteError) throw deleteError;
      }

      // Insert/update income transactions
      if (income && income.length > 0) {
        for (const item of income) {
          if (item.isNew || !item.id) {
            // Insert new income
            const { error: insertError } = await supabase
              .from('service_transactions')
              .insert({
                date: date,
                type: 'income',
                name: item.name || '',
                amount: typeof item.amount === 'string' ? parseFloat(item.amount) : (item.amount || 0),
                description: item.description || '',
                created_by: currentUser?.id
              });

            if (insertError) {
              throw insertError;
            }
          } else {
            // Update existing income
            const { error: updateError } = await supabase
              .from('service_transactions')
              .update({
                date: date,
                type: 'income',
                name: item.name || '',
                amount: typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount,
                description: item.description || '',
              })
              .eq('id', item.id);

            if (updateError) throw updateError;
          }
        }
      }

      // Insert/update expense transactions (filter out empty items)
      const validExpenseItems = (expenses || []).filter(item => item.name && parseFloat(item.amount || 0) > 0);
      if (validExpenseItems.length > 0) {
        for (const item of validExpenseItems) {
          if (item.isNew || !item.id) {
            // Insert new expense
            const { error: insertError } = await supabase
              .from('service_transactions')
              .insert({
                date: date,
                type: 'expense',
                name: item.name || '',
                amount: typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount,
                description: item.description || '',
                created_by: currentUser?.id
              });

            if (insertError) {
              throw insertError;
            }
          } else {
            // Update existing expense
            const { error: updateError } = await supabase
              .from('service_transactions')
              .update({
                date: date,
                type: 'expense',
                name: item.name || '',
                amount: typeof item.amount === 'string' ? parseFloat(item.amount) : item.amount,
                description: item.description || '',
              })
              .eq('id', item.id);

            if (updateError) throw updateError;
          }
        }
      }

      // Update financial summary for the date
      try {
        await dashboardAPI.saveFinancialSummary(date);
      } catch (saveError) {
        console.error('Failed to save financial summary:', saveError);
      }

      return { data: { success: true } };
    } catch (error) {
      console.error('Save service data error:', error);
      throw error;
    }
  },

  // Add daily expense
  addDailyExpense: async (expenseData) => {
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      const { data, error } = await supabase
        .from('daily_expenses')
        .insert([{
          date: expenseData.date,
          description: expenseData.description,
          amount: expenseData.amount,
          notes: expenseData.notes,
          created_by: currentUser?.id
        }])
        .select('*')
        .single();

      if (error) throw error;

      // Update financial summary for the date
      try {
        await dashboardAPI.saveFinancialSummary(expenseData.date);
      } catch (saveError) {
        console.error('Failed to save financial summary:', saveError);
      }

      return { data };
    } catch (error) {
      console.error('Add daily expense error:', error);
      throw error;
    }
  },

  // Update daily expense
  updateDailyExpense: async (expenseId, expenseData) => {
    try {
      const { data, error } = await supabase
        .from('daily_expenses')
        .update({
          date: expenseData.date,
          description: expenseData.description,
          amount: expenseData.amount,
          notes: expenseData.notes
        })
        .eq('id', expenseId)
        .select('*')
        .single();

      if (error) throw error;

      // Update financial summary for the date
      try {
        await dashboardAPI.saveFinancialSummary(expenseData.date);
      } catch (saveError) {
        console.error('Failed to save financial summary:', saveError);
      }

      return { data };
    } catch (error) {
      console.error('Update daily expense error:', error);
      throw error;
    }
  },

  // Delete daily expense
  deleteDailyExpense: async (expenseId) => {
    try {
      // Get expense data first to know the date
      const { data: expense, error: fetchError } = await supabase
        .from('daily_expenses')
        .select('date')
        .eq('id', expenseId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('daily_expenses')
        .delete()
        .eq('id', expenseId);

      if (error) throw error;

      // Update financial summary for the date
      try {
        await dashboardAPI.saveFinancialSummary(expense.date);
      } catch (saveError) {
        console.error('Failed to save financial summary:', saveError);
      }

      return { data: { success: true } };
    } catch (error) {
      console.error('Delete daily expense error:', error);
      throw error;
    }
  },

  // Get daily expenses
  getDailyExpenses: async (params = {}) => {
    try {
      const { date } = params;

      let query = supabase
        .from('daily_expenses')
        .select('*')
        .order('created_at', { ascending: false });

      if (date) {
        query = query.eq('date', date);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { data: data || [] };
    } catch (error) {
      console.error('Get daily expenses error:', error);
      throw error;
    }
  },

  // Get cash expenses (same as daily expenses but with different naming)
  getCashExpenses: async (params = {}) => {
    try {
      const { date, startDate, endDate } = params;

      let query = supabase
        .from('daily_expenses')
        .select('*')
        .order('created_at', { ascending: false });

      if (date) {
        query = query.eq('date', date);
      } else if (startDate && endDate) {
        query = query.gte('date', startDate).lte('date', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { data: data || [] };
    } catch (error) {
      console.error('Get cash expenses error:', error);
      throw error;
    }
  },

  // Add cash expense
  addCashExpense: async (expenseData) => {
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      const { data, error } = await supabase
        .from('daily_expenses')
        .insert([{
          date: expenseData.date,
          description: expenseData.description,
          amount: expenseData.amount,
          notes: expenseData.notes,
          created_by: currentUser?.id
        }])
        .select('*')
        .single();

      if (error) throw error;

      // No RPC side-effects; dashboard summary reads directly from daily_expenses

      return { data };
    } catch (error) {
      console.error('Add cash expense error:', error);
      throw error;
    }
  },

  // Update cash expense
  updateCashExpense: async (expenseId, expenseData) => {
    try {
      const { data, error } = await supabase
        .from('daily_expenses')
        .update({
          date: expenseData.date,
          description: expenseData.description,
          amount: expenseData.amount,
          notes: expenseData.notes
        })
        .eq('id', expenseId)
        .select('*')
        .single();

      if (error) throw error;

      // Update financial summary for the date
      try {
        await dashboardAPI.saveFinancialSummary(expenseData.date);
      } catch (saveError) {
        console.error('Failed to save financial summary:', saveError);
      }

      return { data };
    } catch (error) {
      console.error('Update cash expense error:', error);
      throw error;
    }
  },

  // Delete cash expense
  deleteCashExpense: async (expenseId) => {
    try {
      // Get expense data first to know the date
      const { data: expense, error: fetchError } = await supabase
        .from('daily_expenses')
        .select('date')
        .eq('id', expenseId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('daily_expenses')
        .delete()
        .eq('id', expenseId);

      if (error) throw error;

      // Update financial summary for the date
      try {
        await dashboardAPI.saveFinancialSummary(expense.date);
      } catch (saveError) {
        console.error('Failed to save financial summary:', saveError);
      }

      return { data: { success: true } };
    } catch (error) {
      console.error('Delete cash expense error:', error);
      throw error;
    }
  },

  // Save multiple cash expenses (batch save)
  saveCashExpenses: async (saveData) => {
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      const { expenses, removed, date } = saveData;

      // Delete removed expenses
      if (removed && removed.length > 0) {
        const { error: deleteError } = await supabase
          .from('daily_expenses')
          .delete()
          .in('id', removed);

        if (deleteError) throw deleteError;
      }

      // Insert/update expenses (filter out empty items)
      const validExpenses = (expenses || []).filter(expense => expense.description && parseFloat(expense.amount || 0) > 0);
      const insertedExpenseIds = [];

      if (validExpenses.length > 0) {
        // Process each expense individually
        for (const expense of validExpenses) {
          if (expense.isNew || !expense.id) {
            // Insert new expense (isNew or no id) - let database generate UUID
            const { data: insertedData, error: insertError } = await supabase
              .from('daily_expenses')
              .insert({
                date: date,
                description: expense.description || '',
                amount: parseFloat(expense.amount || 0),
                notes: expense.notes || '',
                created_by: currentUser?.id
              })
              .select('id');

            if (insertError) throw insertError;

            // Store the newly generated UUID
            if (insertedData && insertedData.length > 0) {
              insertedExpenseIds.push({ tempId: expense.tempId || expense.id, realId: insertedData[0].id });
            }
          } else {
            // Update existing expense
            const { error: updateError } = await supabase
              .from('daily_expenses')
              .update({
                date: date,
                description: expense.description || '',
                amount: parseFloat(expense.amount || 0),
                notes: expense.notes || '',
              })
              .eq('id', expense.id);

            if (updateError) throw updateError;
          }
        }

        // Update financial summary for the date
        try {
          await dashboardAPI.saveFinancialSummary(date);
        } catch (saveError) {
          console.error('Failed to save financial summary:', saveError);
        }

        return { data: { success: true, insertedIds: insertedExpenseIds } };
      }

      // If only deletions, still update summary
      if (removed && removed.length > 0) {
        try {
          await dashboardAPI.saveFinancialSummary(date);
        } catch (saveError) {
          console.error('Failed to save financial summary:', saveError);
        }
      }

      return { data: { success: true, insertedIds: insertedExpenseIds } };
    } catch (error) {
      console.error('Save cash expenses error:', error);
      throw error;
    }
  },

  // Save multiple cash income (batch save)
  saveCashIncome: async (saveData) => {
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      const { income, removed, date } = saveData;

      // Delete removed income
      if (removed && removed.length > 0) {
        const { error: deleteError } = await supabase
          .from('daily_revenue_manual')
          .delete()
          .in('id', removed);

        if (deleteError) throw deleteError;
      }

      // Insert/update income (filter out empty items)
      const validIncome = (income || []).filter(item => item.description && parseFloat(item.amount || 0) > 0);
      const insertedIds = [];

      if (validIncome.length > 0) {
        // Process each income individually
        for (const item of validIncome) {
          if (item.isNew || !item.id) {
            // Insert new income (isNew or no id) - let database generate UUID
            const { data: insertedData, error: insertError } = await supabase
              .from('daily_revenue_manual')
              .insert({
                date: date,
                type: 'income',
                description: item.description || '',
                amount: parseFloat(item.amount || 0),
                notes: item.notes || '',
                created_by: currentUser?.id
              })
              .select('id');

            if (insertError) throw insertError;

            // Store the newly generated UUID
            if (insertedData && insertedData.length > 0) {
              insertedIds.push({ tempId: item.tempId || item.id, realId: insertedData[0].id });
            }
          } else {
            // Update existing income
            const { error: updateError } = await supabase
              .from('daily_revenue_manual')
              .update({
                date: date,
                type: 'income',
                description: item.description || '',
                amount: parseFloat(item.amount || 0),
                notes: item.notes || '',
              })
              .eq('id', item.id);

            if (updateError) throw updateError;
          }
        }

        // Update financial summary for the date
        try {
          await dashboardAPI.saveFinancialSummary(date);
        } catch (saveError) {
          console.error('Failed to save financial summary:', saveError);
        }

        return { data: { success: true, insertedIds } };
      }

      // If only deletions, still update summary
      if (removed && removed.length > 0) {
        try {
          await dashboardAPI.saveFinancialSummary(date);
        } catch (saveError) {
          console.error('Failed to save financial summary:', saveError);
        }
      }

      return { data: { success: true } };
    } catch (error) {
      console.error('Save cash income error:', error);
      throw error;
    }
  },

  // Get cash income from daily_revenue_manual table
  getCashIncome: async (params = {}) => {
    try {
      const { date, startDate, endDate } = params;

      let query = supabase
        .from('daily_revenue_manual')
        .select('*')
        .eq('type', 'income')
        .order('created_at', { ascending: false });

      if (date) {
        query = query.eq('date', date);
      } else if (startDate && endDate) {
        query = query.gte('date', startDate).lte('date', endDate);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { data: data || [] };
    } catch (error) {
      console.error('Get cash income error:', error);
      throw error;
    }
  },

  // Add cash income
  addCashIncome: async (incomeData) => {
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      const { data, error } = await supabase
        .from('daily_revenue_manual')
        .insert([{
          date: incomeData.date,
          type: 'income',
          description: incomeData.description,
          amount: incomeData.amount,
          notes: incomeData.notes,
          created_by: currentUser?.id
        }])
        .select('*')
        .single();

      if (error) throw error;

      // Update financial summary for the date
      try {
        await dashboardAPI.saveFinancialSummary(incomeData.date);
      } catch (saveError) {
        console.error('Failed to save financial summary:', saveError);
      }

      return { data };
    } catch (error) {
      console.error('Add cash income error:', error);
      throw error;
    }
  },

  // Update cash income
  updateCashIncome: async (incomeId, incomeData) => {
    try {
      const { data, error } = await supabase
        .from('daily_revenue_manual')
        .update({
          date: incomeData.date,
          type: 'income',
          description: incomeData.description,
          amount: incomeData.amount,
          notes: incomeData.notes
        })
        .eq('id', incomeId)
        .select('*')
        .single();

      if (error) throw error;

      // Update financial summary for the date
      try {
        await dashboardAPI.saveFinancialSummary(incomeData.date);
      } catch (saveError) {
        console.error('Failed to save financial summary:', saveError);
      }

      return { data };
    } catch (error) {
      console.error('Update cash income error:', error);
      throw error;
    }
  },

  // Delete cash income
  deleteCashIncome: async (incomeId) => {
    try {
      // Get income data first to know the date
      const { data: income, error: fetchError } = await supabase
        .from('daily_revenue_manual')
        .select('date')
        .eq('id', incomeId)
        .single();

      if (fetchError) throw fetchError;

      const { error } = await supabase
        .from('daily_revenue_manual')
        .delete()
        .eq('id', incomeId);

      if (error) throw error;

      // Update financial summary for the date
      try {
        await dashboardAPI.saveFinancialSummary(income.date);
      } catch (saveError) {
        console.error('Failed to save financial summary:', saveError);
      }

      return { data: { success: true } };
    } catch (error) {
      console.error('Delete cash income error:', error);
      throw error;
    }
  },

  // Get sold products quantity breakdown for cash sales
  getSoldProductsQuantity: async (params = {}) => {
    try {
      const { dateRange = 'today', selectedDate = new Date(), startDate: providedStartDate, endDate: providedEndDate } = params;

      // Use provided date range or calculate based on filter
      let startDate, endDate;

      if (providedStartDate && providedEndDate) {
        startDate = new Date(providedStartDate);
        endDate = new Date(providedEndDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
      } else {
        const targetDate = new Date(selectedDate);

        switch (dateRange) {
          case 'today':
            startDate = new Date(targetDate);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(targetDate);
            endDate.setHours(23, 59, 59, 999);
            break;
          case 'week':
            endDate = new Date(targetDate);
            endDate.setHours(23, 59, 59, 999);
            startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - 6);
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'month':
            const year = targetDate.getFullYear();
            const month = targetDate.getMonth();
            startDate = new Date(year, month, 1);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(year, month + 1, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
          default:
            startDate = new Date(targetDate);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(targetDate);
            endDate.setHours(23, 59, 59, 999);
        }
      }

      // Get stock sales for the period
      const { data: stockSalesData, error: stockSalesError } = await supabase
        .from('sales')
        .select(`
           quantity,
           products (
             id,
             name,
             cost,
             price
           )
         `)
        .gte('sale_date', startDate.toISOString())
        .lte('sale_date', endDate.toISOString());

      if (stockSalesError) {
        console.error('Error fetching stock sales:', stockSalesError);
        throw stockSalesError;
      }

      // Group by product and sum quantities
      const productQuantities = {};
      (stockSalesData || []).forEach(sale => {
        const product = sale.products;
        if (product) {
          const key = product.id;
          if (!productQuantities[key]) {
            productQuantities[key] = {
              id: product.id,
              name: product.name,
              quantity: 0
            };
          }
          productQuantities[key].quantity += sale.quantity || 0;
        }
      });

      // Convert to array and sort by quantity descending
      const soldProducts = Object.values(productQuantities)
        .sort((a, b) => b.quantity - a.quantity);

      return { data: soldProducts };
    } catch (error) {
      console.error('Get sold products quantity error:', error);
      throw error;
    }
  },

  // Get car sold products quantity breakdown for car sales
  getCarSoldProductsQuantity: async (params = {}) => {
    try {
      const { dateRange = 'today', selectedDate = new Date(), startDate: providedStartDate, endDate: providedEndDate } = params;

      // Use provided date range or calculate based on filter
      let startDate, endDate;

      if (providedStartDate && providedEndDate) {
        startDate = new Date(providedStartDate);
        endDate = new Date(providedEndDate);
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
      } else {
        const targetDate = new Date(selectedDate);

        switch (dateRange) {
          case 'today':
            startDate = new Date(targetDate);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(targetDate);
            endDate.setHours(23, 59, 59, 999);
            break;
          case 'week':
            endDate = new Date(targetDate);
            endDate.setHours(23, 59, 59, 999);
            startDate = new Date(endDate);
            startDate.setDate(startDate.getDate() - 6);
            startDate.setHours(0, 0, 0, 0);
            break;
          case 'month':
            const year = targetDate.getFullYear();
            const month = targetDate.getMonth();
            startDate = new Date(year, month, 1);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(year, month + 1, 0);
            endDate.setHours(23, 59, 59, 999);
            break;
          default:
            startDate = new Date(targetDate);
            startDate.setHours(0, 0, 0, 0);
            endDate = new Date(targetDate);
            endDate.setHours(23, 59, 59, 999);
        }
      }

      // Get car sales for the period
      const { data: carSalesData, error: carSalesError } = await supabase
        .from('car_sales')
        .select(`
           quantity,
           products (
             id,
             name,
             cost,
             price
           ),
           cars (
             id,
             plate
           )
         `)
        .gte('sale_date', startDate.toISOString())
        .lte('sale_date', endDate.toISOString());

      if (carSalesError) {
        console.error('Error fetching car sales:', carSalesError);
        throw carSalesError;
      }

      // Group by product and car, sum quantities
      const productCarQuantities = {};
      (carSalesData || []).forEach(sale => {
        const product = sale.products;
        const car = sale.cars;
        if (product && car) {
          const key = `${product.id}-${car.id}`;
          if (!productCarQuantities[key]) {
            productCarQuantities[key] = {
              productId: product.id,
              productName: product.name,
              carId: car.id,
              carPlate: car.plate,
              quantity: 0
            };
          }
          productCarQuantities[key].quantity += sale.quantity || 0;
        }
      });

      // Convert to array and sort by quantity descending
      const carSoldProducts = Object.values(productCarQuantities)
        .sort((a, b) => b.quantity - a.quantity);

      return { data: carSoldProducts };
    } catch (error) {
      console.error('Get car sold products quantity error:', error);
      throw error;
    }
  },

  // Update financial summary for a specific date (legacy wrapper)
  updateDailyRevenue: async (date) => {
    try {
      // Use the new saveFinancialSummary function
      return await dashboardAPI.saveFinancialSummary(date);
    } catch (error) {
      console.error('Update daily revenue error:', error);
      throw error;
    }
  },

  // Get dashboard settings for user
  getDashboardSettings: async () => {
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      const { data, error } = await supabase
        .from('dashboard_settings')
        .select('*')
        .eq('user_id', currentUser?.id)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows found

      return { data: { settings: data } };
    } catch (error) {
      console.error('Get dashboard settings error:', error);
      throw error;
    }
  },

  // Update dashboard settings for user
  updateDashboardSettings: async (settingsData) => {
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      const { data, error } = await supabase
        .from('dashboard_settings')
        .upsert({
          user_id: currentUser?.id,
          ...settingsData
        })
        .select()
        .single();

      if (error) throw error;
      return { data: { settings: data } };
    } catch (error) {
      console.error('Update dashboard settings error:', error);
      throw error;
    }
  },

  // Get financial summary from daily_financial_summary table for a specific period
  getFinancialSummaryByPeriod: async (period = 'month') => {
    try {
      const today = new Date();

      let startDate, endDate;

      if (period === 'daily') {
        // Get last 30 days for daily view
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today);
        endDate.setHours(23, 59, 59, 999);
      } else if (period === 'weekly') {
        // Get last 12 weeks for weekly view
        startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 84); // 12 weeks
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today);
        endDate.setHours(23, 59, 59, 999);
      } else if (period === 'monthly') {
        // Get last 12 months for monthly view
        startDate = new Date(today);
        startDate.setMonth(startDate.getMonth() - 12);
        startDate.setDate(1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today);
        endDate.setHours(23, 59, 59, 999);
      } else if (period === 'yearly') {
        // Get last 5 years for yearly view
        startDate = new Date(today);
        startDate.setFullYear(startDate.getFullYear() - 5);
        startDate.setMonth(0, 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(today);
        endDate.setHours(23, 59, 59, 999);
      }

      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('daily_financial_summary')
        .select('*')
        .gte('date', startDateStr)
        .lte('date', endDateStr)
        .order('date', { ascending: true });

      if (error) throw error;

      // Calculate totals
      const totalIncome = data.reduce((sum, day) => sum + parseFloat(day.total_income || 0), 0);
      const totalExpense = data.reduce((sum, day) => sum + parseFloat(day.total_expense || 0), 0);
      const netAmount = totalIncome - totalExpense;

      return {
        data: {
          period,
          startDate: startDateStr,
          endDate: endDateStr,
          totalIncome,
          totalExpense,
          netAmount,
          dailyData: data || []
        }
      };
    } catch (error) {
      console.error('Get financial summary by period error:', error);
      throw error;
    }
  },

  // Get latest financial summary for today
  getTodayFinancialSummary: async () => {
    try {
      const today = new Date().toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('daily_financial_summary')
        .select('*')
        .eq('date', today)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      return {
        data: data || {
          date: today,
          total_income: 0,
          total_expense: 0,
          net_amount: 0
        }
      };
    } catch (error) {
      console.error('Get today financial summary error:', error);
      throw error;
    }
  },

  // Get financial data grouped by period
  getFinancialDataByPeriod: async (groupBy = 'day', days = 30) => {
    try {
      const today = new Date();
      const startDate = new Date(today);
      startDate.setDate(startDate.getDate() - days);
      startDate.setHours(0, 0, 0, 0);

      const { data, error } = await supabase
        .from('daily_financial_summary')
        .select('*')
        .gte('date', startDate.toISOString().split('T')[0])
        .lte('date', today.toISOString().split('T')[0])
        .order('date', { ascending: true });

      if (error) throw error;

      // Group data by week or month if needed
      const groupedData = [];
      if (groupBy === 'week') {
        const weeks = {};
        data.forEach(item => {
          const date = new Date(item.date);
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() - date.getDay());
          const weekKey = weekStart.toISOString().split('T')[0];

          if (!weeks[weekKey]) {
            weeks[weekKey] = {
              date: weekKey,
              total_income: 0,
              total_expense: 0,
              net_amount: 0,
              days: []
            };
          }
          weeks[weekKey].total_income += parseFloat(item.total_income || 0);
          weeks[weekKey].total_expense += parseFloat(item.total_expense || 0);
          weeks[weekKey].days.push(item);
        });

        Object.values(weeks).forEach(week => {
          week.net_amount = week.total_income - week.total_expense;
          groupedData.push(week);
        });
      } else if (groupBy === 'month') {
        const months = {};
        data.forEach(item => {
          const date = new Date(item.date);
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

          if (!months[monthKey]) {
            months[monthKey] = {
              date: monthKey + '-01',
              total_income: 0,
              total_expense: 0,
              net_amount: 0,
              days: []
            };
          }
          months[monthKey].total_income += parseFloat(item.total_income || 0);
          months[monthKey].total_expense += parseFloat(item.total_expense || 0);
          months[monthKey].days.push(item);
        });

        Object.values(months).forEach(month => {
          month.net_amount = month.total_income - month.total_expense;
          groupedData.push(month);
        });
      } else {
        // Day by day
        groupedData.push(...data);
      }

      return { data: groupedData };
    } catch (error) {
      console.error('Get financial data by period error:', error);
      throw error;
    }
  }
};

// Employees API
export const employeesAPI = {
  // Get all employees
  getEmployees: async (params = {}) => {
    try {
      let query = supabase
        .from('employees')
        .select('*')
        .eq('is_active', true);

      if (params.search) {
        query = query.or(`first_name.ilike.%${params.search}%, last_name.ilike.%${params.search}%`);
      }

      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      return { data: { employees: data } };
    } catch (error) {
      console.error('Get employees error:', error);
      throw error;
    }
  },

  // Get single employee
  getEmployee: async (id) => {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('*')
        .eq('id', id)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return { data };
    } catch (error) {
      console.error('Get employee error:', error);
      throw error;
    }
  },

  // Create employee
  createEmployee: async (employeeData) => {
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      const dataWithUser = {
        ...employeeData,
        created_by: currentUser?.id
      };

      const { data, error } = await supabase
        .from('employees')
        .insert([dataWithUser])
        .select()
        .single();

      if (error) throw error;
      return { data };
    } catch (error) {
      console.error('Create employee error:', error);
      throw error;
    }
  },

  // Update employee
  updateEmployee: async (id, employeeData) => {
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      const dataWithUser = {
        ...employeeData,
        updated_by: currentUser?.id
      };

      const { data, error } = await supabase
        .from('employees')
        .update(dataWithUser)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data };
    } catch (error) {
      console.error('Update employee error:', error);
      throw error;
    }
  },

  // Delete employee (soft delete)
  deleteEmployee: async (id) => {
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      const { data, error } = await supabase
        .from('employees')
        .update({ is_active: false, updated_by: currentUser?.id })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return { data };
    } catch (error) {
      console.error('Delete employee error:', error);
      throw error;
    }
  }
};

// Employee Bonuses API
export const employeeBonusesAPI = {
  // Get bonuses with filters
  getBonuses: async (params = {}) => {
    try {
      let query = supabase
        .from('employee_bonuses')
        .select(`
          id,
          employee_id,
          amount,
          bonus_date,
          employees:employee_id (
            id,
            first_name,
            last_name
          )
        `);

      if (params.employeeId) {
        query = query.eq('employee_id', params.employeeId);
      }

      if (params.startDate && params.endDate) {
        query = query.gte('bonus_date', params.startDate).lte('bonus_date', params.endDate);
      } else if (params.date) {
        query = query.eq('bonus_date', params.date);
      }

      query = query.order('bonus_date', { ascending: false });

      const { data, error } = await query;

      if (error) throw error;

      return { data: { bonuses: data } };
    } catch (error) {
      console.error('Get bonuses error:', error);
      throw error;
    }
  },

  // Get single bonus
  getBonus: async (id) => {
    try {
      const { data, error } = await supabase
        .from('employee_bonuses')
        .select(`
          id,
          employee_id,
          amount,
          bonus_date,
          employees:employee_id (
            id,
            first_name,
            last_name
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return { data };
    } catch (error) {
      console.error('Get bonus error:', error);
      throw error;
    }
  },

  // Create bonus
  createBonus: async (bonusData) => {
    try {
      const { data: { user: currentUser }, error: userError } = await supabase.auth.getUser();

      if (userError) throw userError;

      const dataWithUser = {
        ...bonusData,
        created_by: currentUser?.id
      };

      const { data, error } = await supabase
        .from('employee_bonuses')
        .insert([dataWithUser])
        .select(`
          id,
          employee_id,
          amount,
          bonus_date,
          employees:employee_id (
            id,
            first_name,
            last_name
          )
        `)
        .single();

      if (error) throw error;
      return { data };
    } catch (error) {
      console.error('Create bonus error:', error);
      throw error;
    }
  },

  // Update bonus
  updateBonus: async (id, bonusData) => {
    try {
      const { data, error } = await supabase
        .from('employee_bonuses')
        .update(bonusData)
        .eq('id', id)
        .select(`
          id,
          employee_id,
          amount,
          bonus_date,
          employees:employee_id (
            id,
            first_name,
            last_name
          )
        `)
        .single();

      if (error) throw error;
      return { data };
    } catch (error) {
      console.error('Update bonus error:', error);
      throw error;
    }
  },

  // Delete bonus
  deleteBonus: async (id) => {
    try {
      const { error } = await supabase
        .from('employee_bonuses')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { data: { success: true } };
    } catch (error) {
      console.error('Delete bonus error:', error);
      throw error;
    }
  },

  // Get bonus statistics for a date range
  getBonusStats: async (startDate, endDate) => {
    try {
      const { data, error } = await supabase
        .from('employee_bonuses')
        .select('amount, employee_id')
        .gte('bonus_date', startDate)
        .lte('bonus_date', endDate);

      if (error) throw error;

      const totalAmount = data.reduce((sum, bonus) => sum + parseFloat(bonus.amount || 0), 0);
      const bonusCount = data.length;
      const uniqueEmployees = new Set(data.map(b => b.employee_id)).size;

      return {
        data: {
          totalAmount,
          bonusCount,
          uniqueEmployees,
          bonuses: data
        }
      };
    } catch (error) {
      console.error('Get bonus stats error:', error);
      throw error;
    }
  },

  // Daily Financial Summary Management
  saveDailyFinancialSummary: async (date, summaryData) => {
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user?.id) throw new Error('User not authenticated');

      const { totalIncome, totalExpense, netAmount } = summaryData;
      const dateStr = date instanceof Date ? date.toISOString().split('T')[0] : date;

      // Check if record exists for this date
      const { data: existing } = await supabase
        .from('daily_financial_summary')
        .select('*')
        .eq('date', dateStr)
        .single();

      if (existing) {
        // Update existing record
        const { data, error } = await supabase
          .from('daily_financial_summary')
          .update({
            total_income: totalIncome,
            total_expense: totalExpense,
            net_amount: netAmount,
            updated_at: new Date().toISOString()
          })
          .eq('date', dateStr)
          .select()
          .single();

        if (error) throw error;
        return { data };
      } else {
        // Insert new record
        const { data, error } = await supabase
          .from('daily_financial_summary')
          .insert({
            date: dateStr,
            total_income: totalIncome,
            total_expense: totalExpense,
            net_amount: netAmount,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select()
          .single();

        if (error) throw error;
        return { data };
      }
    } catch (error) {
      console.error('Save daily financial summary error:', error);
      throw error;
    }
  },

  getDailyFinancialSummary: async (startDate, endDate) => {
    try {
      const startDateStr = startDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('daily_financial_summary')
        .select('*')
        .gte('date', startDateStr)
        .lte('date', endDateStr)
        .order('date', { ascending: false });

      if (error) throw error;

      return { data: data || [] };
    } catch (error) {
      console.error('Get daily financial summary error:', error);
      throw error;
    }
  }
};

const apiUtils = { supabase };
export default apiUtils;
